import { Component, OnInit, HostListener, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { EsocService } from '../../services/e-soc/esoc.service';
import { MessageHandlerService } from '../../services/message-handler.service';
import { DatePipe } from '@angular/common';
import { UtilService } from '../../services/util/util.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DOB_DATE_FORMAT, DOB_NOT_MATCH, PDFJS_WORKER_URL } from '../../common/Constants';
import { endOfToday } from '../../common/calendar-max-date.util';
import { environment } from 'src/environments/environment';
import { tickCheckBoxByExportValue } from '../pdf-checkbox.utils';
import {
  FillFieldKind,
  FillFieldTag,
  LABEL_GAP,
  LABEL_ROOM,
  PageBox,
  PageObstacle,
  currentFieldSpots,
  fillFieldLabel,
  fillFieldTagText,
  labelBox,
  placeLabels,
} from '../fill-field-label.utils';

/** Somewhere the signer is taken on the signing page: a field they fill in, or one of their signature boxes (IOHB-1884). */
type SigningStop = { kind: 'fill'; name: string } | { kind: 'signature'; index: number };

/** Form order: page by page, then top to bottom (within a few pixels counts as the same line), then left to right. */
const byFormOrder = (a: { page: number; top: number; left: number }, b: { page: number; top: number; left: number }) =>
  a.page - b.page || Math.round(a.top / 5) - Math.round(b.top / 5) || a.left - b.left;

/** Room kept above a signature box on a phone, where the "✎ Sign here" tag floats (34px up) and bobs (IOHB-1632), so
 *  field labels stay clear of it (IOHB-1886). */
const SIGN_HERE_TAG_ROOM = 38;

@Component({
  selector: 'app-esign-verification',
  templateUrl: './esign-verification.component.html',
  styleUrls: ['./esign-verification.component.scss']
})
export class EsignVerificationComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  code: string = '';
  linkDetails: any = null;
  linkExpired: boolean = false;
  isSigned: boolean = false;
  isVerified: boolean = false;
  // DOB brute-force lockout: set when the backend reports the link is temporarily locked.
  dobLocked: boolean = false;
  dobLockMessage: string = '';
  loading: boolean = false;
  sessionToken: string = '';
  showConsentOverlay: boolean = true;
  // Mobile only: toggles the full-screen Electronic Record & Signature Disclosure viewer,
  // opened from the "View disclosure" link next to the mobile consent checkbox.
  showEsignDisclosure: boolean = false;

  dob: any = null;
  consent: boolean = false;
  submittingSign: boolean = false;

  pdfUrl: SafeResourceUrl | null = null;
  rawPdfUrl: string = '';

  innerWidth = window.innerWidth;
  // Primary input is touch (phone/tablet) — computed once. OR'd with a width fallback
  // so phones + tablets (any orientation) get the touch signing UX, while a mouse
  // desktop stays on the side-by-side console at any window size (IOHB-1632).
  private readonly isTouchPrimary =
    typeof window !== 'undefined' && !!window.matchMedia &&
    window.matchMedia('(pointer: coarse)').matches;

  get isMobile(): boolean {
    return this.isTouchPrimary || this.innerWidth <= 991;
  }
  dobDateFormat = DOB_DATE_FORMAT;
  maxDate: Date = endOfToday();

  pdfDoc: any = null;
  numPages: number = 0;
  pages: number[] = [];
  sigFieldInfo: any = null;
  sigFieldsList: any[] = [];
  currentSigIndex: number = 0;
  hasScrolledInitially: boolean = false;

  now = new Date();
  timeOffset: number = 0;
  timeIntervalId: any = null;

  sigX = 50;
  sigY = 50;
  // The signature takes the size and position of the document's own signature field. A signer cannot
  // resize or move it: resizing scaled the box about its centre and pushed it outside that field, while
  // the sealed signature stayed in the field regardless (IOHB-1898).
  sigHeight: number = 60;
  sigWidth: number = 150;

  zoomScale: number = 1;
  zoomX = 15;
  zoomY = 15;
  isDraggingZoom = false;
  private zoomDragStartX = 0;
  private zoomDragStartY = 0;
  private zoomPanelStartX = 0;
  private zoomPanelStartY = 0;
  private initialTouchDistance = 0;
  private initialZoomScale = 1;
  private isPinching = false;
  private tempZoomScale = 1;
  // Pinch focal point (in .pdf-pages-container local coords) so zoom anchors at the
  // fingers/centre instead of the top-left corner (IOHB-1632).
  private pinchOriginX: number | null = null;
  private pinchOriginY: number | null = null;


  @ViewChild('pdfViewerScroll') set scrollContent(content: ElementRef<HTMLDivElement>) {
    if (content) {
      this.pdfViewerScroll = content;
      this.setupPinchZoom();
    }
  }
  pdfViewerScroll: ElementRef<HTMLDivElement>;

  @ViewChild('sigCanvas') set content(content: ElementRef<HTMLCanvasElement>) {
    if (content) {
      this.sigCanvas = content;
      this.initCanvas();
    }
  }
  sigCanvas: ElementRef<HTMLCanvasElement>;
  ctx: CanvasRenderingContext2D | null = null;
  isDrawing = false;
  lastX = 0;
  lastY = 0;

  signatureMode: 'draw' | 'type' | 'upload' = 'upload';
  lockedSignatureMode: 'draw' | 'type' | 'upload' | null = null;
  typedSignature: string = '';
  uploadedSignature: string | null = null;
  drawnSignature: string | null = null;
  drawnSignatureFull: string | null = null;
  scrollTimeout: any = null;
  geolocation: { latitude: number; longitude: number } | null = null;

  requestGeolocation() {
    if (window.navigator && window.navigator.geolocation) {
      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          this.geolocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
        },
        (error) => {
          console.warn('Geolocation permission denied or failed:', error);
        }
      );
    }
  }

  setSignatureMode(mode: 'draw' | 'type' | 'upload') {
    this.signatureMode = mode;
    if (mode === 'draw') {
      setTimeout(() => this.initCanvas(), 100);
    }
    // Tabs differ in height — re-measure so the PDF scroll's reserved bottom room stays correct.
    setTimeout(() => this.updateMobileSheetPad(), 120);
  }

  initCanvas() {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;
    this.ctx = canvas.getContext('2d');
    if (this.ctx) {
      this.ctx.strokeStyle = '#000000';
      this.ctx.lineWidth = 2.5;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
    }

    const targetWidth = canvas.parentElement.clientWidth || 300;
    const targetHeight = 150;

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // If we had a signature, redraw it in its original coordinates
      if (this.drawnSignatureFull) {
        const img = new Image();
        img.onload = () => {
          this.ctx?.drawImage(img, 0, 0);
        };
        img.src = this.drawnSignatureFull;
      }
    }
  }

  startDrawing(event: MouseEvent) {
    if (!this.ctx || this.lockedSignatureMode) return;
    this.isDrawing = true;
    const rect = this.sigCanvas.nativeElement.getBoundingClientRect();
    this.lastX = event.clientX - rect.left;
    this.lastY = event.clientY - rect.top;
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing || !this.ctx) return;
    const rect = this.sigCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();

    this.lastX = x;
    this.lastY = y;
  }

  startDrawingTouch(event: TouchEvent) {
    if (!this.ctx || this.lockedSignatureMode || event.touches.length === 0) return;
    this.isDrawing = true;
    const rect = this.sigCanvas.nativeElement.getBoundingClientRect();
    const touch = event.touches[0];
    this.lastX = touch.clientX - rect.left;
    this.lastY = touch.clientY - rect.top;
    event.preventDefault();
  }

  drawTouch(event: TouchEvent) {
    if (!this.isDrawing || !this.ctx || event.touches.length === 0) return;
    const rect = this.sigCanvas.nativeElement.getBoundingClientRect();
    const touch = event.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();

    this.lastX = x;
    this.lastY = y;
    event.preventDefault();
  }

  stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.saveCanvasState();
    }
  }

  scrollToSignatureField(index: number) {
    if (!this.sigFieldsList || this.sigFieldsList.length <= index) return;
    const sigBox = this.sigFieldsList[index];
    const pageNum = sigBox.pageNumber;
    const canvas = document.getElementById('pdf-canvas-' + pageNum);
    if (canvas && this.pdfViewerScroll) {
      const scrollEl = this.pdfViewerScroll.nativeElement;
      const scrollRect = scrollEl.getBoundingClientRect();
      const wrapper = canvas.parentElement;
      if (wrapper) {
        const isMobile = this.isMobile;
        // Land the active field high in the viewport instead of the middle: near the top on
        // mobile (~1/6 down, clear of the top bar) and in the upper third on desktop (~1/3),
        // so the signature/field isn't sitting low on the screen. Larger divisor = higher.
        const scrollDivisor = isMobile ? 6 : 3;
        const targetScrollTop = wrapper.offsetTop + sigBox.displayY - (scrollRect.height / scrollDivisor) + (sigBox.displayHeight / 2);
        scrollEl.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      }
    }
  }

  updateActiveSignature() {
    // Signing in the pad puts the signer back on their signature, wherever they were in the form (IOHB-1884).
    if (this.activeFillFieldName !== null) this.setActiveFillField(null);
    if (this.sigFieldsList?.[this.currentSigIndex]) {
      const image = this.getSignatureImage();
      this.sigFieldsList[this.currentSigIndex].signatureImage = image;
      if (image) {
        this.sigFieldsList[this.currentSigIndex].signatureMode = this.signatureMode;
      } else {
        delete this.sigFieldsList[this.currentSigIndex].signatureMode;
      }
      // Locking is deferred to an explicit apply/navigation (applyMobileSignature,
      // applyDesktopSignature, loadSignatureForStep) — not on every live edit — so the
      // first signature isn't cut off mid-stroke/keystroke the moment it's drawn (IOHB-1632).
      this.cdr.detectChanges();
    }
  }

  checkLockedSignatureMode() {
    const firstSignedField = (this.sigFieldsList || []).find(s => !!s.signatureImage && !!s.signatureMode);
    if (firstSignedField) {
      this.lockedSignatureMode = firstSignedField.signatureMode;
      this.signatureMode = this.lockedSignatureMode;
    } else {
      this.lockedSignatureMode = null;
    }
  }

  showRedoConfirm = false;

  triggerRedo(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.showRedoConfirm = true;
  }

  confirmRedo() {
    this.showRedoConfirm = false;
    this.redoAllSignatures();
  }

  redoAllSignatures() {
    if (this.sigFieldsList) {
      this.sigFieldsList.forEach(s => {
        s.signatureImage = null;
        delete s.signatureMode;
      });
    }
    this.lockedSignatureMode = null;
    this.setActiveFillField(null);
    this.currentSigIndex = 0;
    this.loadSignatureForStep(0);
    this.scrollToSignatureField(0);
    this.cdr.detectChanges();
  }

  loadSignatureForStep(idx: number) {
    if (!this.sigFieldsList || this.sigFieldsList.length <= idx) return;
    const sigBox = this.sigFieldsList[idx];

    let img = sigBox.signatureImage || '';
    let mode = sigBox.signatureMode;
    if (!img) {
      const firstSignedField = (this.sigFieldsList || []).find(s => !!s.signatureImage && !!s.signatureMode);
      if (firstSignedField) {
        img = firstSignedField.signatureImage;
        mode = firstSignedField.signatureMode;
      }
    }

    // Re-establish the lock when landing on any field: once a signature has been
    // committed on some field, every field must reuse it (IOHB-1632).
    this.checkLockedSignatureMode();
    if (this.lockedSignatureMode) {
      this.signatureMode = this.lockedSignatureMode;
    } else if (mode) {
      this.signatureMode = mode;
    }

    // Reset inputs
    this.drawnSignature = null;
    this.drawnSignatureFull = null;
    this.typedSignature = '';
    this.uploadedSignature = null;

    if (this.sigCanvas) {
      this.clearCanvas(true);
    }

    if (img) {
      if (img.startsWith('data:image/')) {
        this.uploadedSignature = img;
        this.drawnSignature = img;
        this.drawnSignatureFull = img;

        setTimeout(() => {
          if (this.sigCanvas && this.ctx) {
            const canvas = this.sigCanvas.nativeElement;
            const image = new Image();
            image.onload = () => this.drawSignatureCentered(canvas, image);
            image.src = img;
          }
        }, 150);
      } else {
        this.typedSignature = img;
      }
    }
    this.cdr.detectChanges();
  }

  showMobileBottomSheet = false;
  // Bottom padding (px) reserved on the PDF scroll while the signature sheet is open, so the WHOLE
  // document (including its bottom) can be scrolled clear of the sheet — not just the active field.
  // 0 = sheet closed (the CSS falls back to the smaller, bottom-bar-sized padding). IOHB-1676.
  mobileSheetPad = 0;

  openMobileBottomSheet() {
    this.showMobileBottomSheet = true;
    if (this.signatureMode === 'draw') {
      setTimeout(() => this.initCanvas(), 150);
    }
    // Reserve scroll room equal to the open sheet's height, then keep the active field visible above
    // the sheet so the signer can see where they are signing (IOHB-1632 / IOHB-1676).
    setTimeout(() => { this.updateMobileSheetPad(); this.scrollActiveFieldAboveSheet(); }, 250);
  }

  /** Mobile: measure the open signature sheet so the PDF scroll reserves matching bottom room. Called
   *  when the sheet opens and when its tab changes (Draw/Type/Upload have different heights). */
  updateMobileSheetPad() {
    if (!this.isMobile || !this.showMobileBottomSheet) { this.mobileSheetPad = 0; return; }
    const sheet = document.querySelector('.mobile-bottom-sheet') as HTMLElement | null;
    this.mobileSheetPad = sheet ? Math.round(sheet.getBoundingClientRect().height) + 24 : 0;
  }

  /** Mobile: scroll the active signature field near the top of the viewer so it is
   *  not hidden behind the bottom sheet (IOHB-1632). */
  private scrollActiveFieldAboveSheet() {
    if (!this.isMobile) return;
    const sigBox = this.sigFieldsList?.[this.currentSigIndex];
    if (!sigBox || !this.pdfViewerScroll) return;
    const canvas = document.getElementById('pdf-canvas-' + sigBox.pageNumber);
    const wrapper = canvas?.parentElement;
    if (!wrapper) return;
    const targetScrollTop = wrapper.offsetTop + (sigBox.displayY || 0) - 24;
    this.pdfViewerScroll.nativeElement.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  }

  closeMobileBottomSheet() {
    this.showMobileBottomSheet = false;
    this.mobileSheetPad = 0;
  }

  clearMobileSignature() {
    this.clearCanvas();
    this.typedSignature = '';
    this.uploadedSignature = null;
    this.updateActiveSignature();
  }

  applyMobileSignature() {
    this.updateActiveSignature();
    this.checkLockedSignatureMode();
    this.closeMobileBottomSheet();
  }

  /** Desktop: commit the current field's signature, lock it so later fields reuse it
   *  (revealing Redo), then advance to the next field in the form (IOHB-1632, IOHB-1884). */
  applyDesktopSignature() {
    this.updateActiveSignature();
    this.checkLockedSignatureMode();
    this.nextStop();
  }

  /** Re-render a stored signature centred in the pad so a signature drawn mid-canvas
   *  doesn't snap to the top-left on revisit (IOHB-1632). Preview-only — the placement
   *  stamped into the document is unaffected. */
  drawSignatureCentered(canvas: HTMLCanvasElement, image: HTMLImageElement) {
    if (!this.ctx) return;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    const dx = Math.max(0, (canvas.width - image.width) / 2);
    const dy = Math.max(0, (canvas.height - image.height) / 2);
    this.ctx.drawImage(image, dx, dy);
  }

  getCompletedSignaturesCount(): number {
    return (this.sigFieldsList || []).filter(s => !!s.signatureImage).length;
  }

  hasSignatureForStep(idx: number): boolean {
    return !!this.sigFieldsList?.[idx]?.signatureImage;
  }

  jumpToActiveSignature() {
    if (this.activeFillFieldName !== null) {
      this.scrollToFillField(this.activeFillFieldName);
      return;
    }
    this.scrollToSignatureField(this.currentSigIndex);
  }

  handleMobilePrimaryAction() {
    // Once the page knows where the signer's fields are, the button takes them through the form (IOHB-1884).
    if (this.signingStops.length > 0) {
      const action = this.mobilePrimaryAction();
      if (action === 'finish') {
        this.submitSignature();
      } else if (action === 'sign') {
        this.openMobileBottomSheet();
      } else {
        const target = this.nextStopTarget();
        if (target) this.goToStop(target, true);
      }
      return;
    }

    if (this.allSignaturesCompleted()) {
      this.submitSignature();
      return;
    }

    if (!this.hasSignatureForStep(this.currentSigIndex)) {
      this.openMobileBottomSheet();
      return;
    }

    const nextUnsignedIdx = this.sigFieldsList.findIndex(s => !s.signatureImage);
    if (nextUnsignedIdx !== -1) {
      this.currentSigIndex = nextUnsignedIdx;
      this.loadSignatureForStep(this.currentSigIndex);
      setTimeout(() => {
        this.scrollToSignatureField(this.currentSigIndex);
        this.openMobileBottomSheet();
      }, 250);
    }
  }

  selectSigStep(idx: number) {
    this.setActiveFillField(null);
    const isMobile = this.isMobile;
    if (this.currentSigIndex === idx && isMobile) {
      if (this.consent) {
        this.openMobileBottomSheet();
      }
      return;
    }
    this.currentSigIndex = idx;
    this.loadSignatureForStep(idx);
    setTimeout(() => {
      this.scrollToSignatureField(idx);
    }, 250);
    if (isMobile && this.consent) {
      this.openMobileBottomSheet();
    }
  }

  nextSignatureStep() {
    if (this.currentSigIndex < this.sigFieldsList.length - 1) {
      this.currentSigIndex++;
      this.loadSignatureForStep(this.currentSigIndex);
      setTimeout(() => {
        this.scrollToSignatureField(this.currentSigIndex);
      }, 300);
    }
  }

  prevSignatureStep() {
    if (this.currentSigIndex > 0) {
      this.currentSigIndex--;
      this.loadSignatureForStep(this.currentSigIndex);
      setTimeout(() => {
        this.scrollToSignatureField(this.currentSigIndex);
      }, 300);
    }
  }

  // ---- IOHB-1884: taking the signer through their fields and signatures in form order ----
  // Until the page has worked out where the signer's fields are (signingStops is empty), each of these falls back to
  // stepping through the signatures as before.

  /** Where the signer is in signingStops: the fill-in field they're on, or else their active signature. */
  private currentStopIndex(): number {
    const fillFieldName = this.activeFillFieldName;
    return this.signingStops.findIndex(stop =>
      fillFieldName !== null
        ? stop.kind === 'fill' && stop.name === fillFieldName
        : stop.kind === 'signature' && stop.index === this.currentSigIndex,
    );
  }

  /** Where "Next" goes: the next field, or signature still to sign, further down the form. From the end of the form it
   *  goes back to the first signature still to sign, and when nothing is left to do there's nowhere to go. */
  nextStopTarget(includeEmptyRequired = true): SigningStop | null {
    const current = this.currentStopIndex();
    const unsigned = (stop: SigningStop) => stop.kind === 'signature' && !this.sigFieldsList[stop.index]?.signatureImage;
    const later = this.signingStops.slice(current + 1).find(stop => stop.kind === 'fill' || unsigned(stop));
    if (later) return later;
    // Past the last stop, go back to anything Finish would still refuse: a signature not yet signed, or
    // a required field left empty. Only signatures used to count here, so a required field sitting above
    // the signature the signer started on could never be reached with Next — yet Finish then stopped
    // them and sent them straight to it.
    const emptyRequired = includeEmptyRequired ? new Set(this.findEmptyRequiredFillFields()) : new Set<string>();
    const stillNeeded = (stop: SigningStop) =>
      unsigned(stop) || (stop.kind === 'fill' && emptyRequired.has(stop.name));
    return this.signingStops.find((stop, i) => i !== current && stillNeeded(stop)) || null;
  }

  hasNextStop(): boolean {
    if (this.signingStops.length === 0) return this.currentSigIndex < this.sigFieldsList.length - 1;
    return this.nextStopTarget() !== null;
  }

  hasPrevStop(): boolean {
    if (this.signingStops.length === 0) return this.currentSigIndex > 0;
    return this.currentStopIndex() > 0;
  }

  nextStop() {
    if (this.signingStops.length === 0) {
      this.nextSignatureStep();
      return;
    }
    const target = this.nextStopTarget();
    if (target) this.goToStop(target, false);
  }

  prevStop() {
    if (this.signingStops.length === 0) {
      this.prevSignatureStep();
      return;
    }
    const current = this.currentStopIndex();
    if (current > 0) this.goToStop(this.signingStops[current - 1], false);
  }

  /** "Step X of N" counts every field and signature the signer is taken to. */
  currentStepNumber(): number {
    if (this.signingStops.length === 0) return this.currentSigIndex + 1;
    return Math.max(0, this.currentStopIndex()) + 1;
  }

  stepCount(): number {
    return this.signingStops.length || this.sigFieldsList.length;
  }

  /** Takes the signer to a field they fill in, or to one of their signatures (opening the pad, when asked, as the
   *  phone's button does). */
  goToStop(stop: SigningStop, openSignaturePad: boolean) {
    if (stop.kind === 'fill') {
      this.setActiveFillField(stop.name);
      this.scrollToFillField(stop.name);
      this.cdr.detectChanges();
      return;
    }
    this.setActiveFillField(null);
    this.currentSigIndex = stop.index;
    this.loadSignatureForStep(stop.index);
    setTimeout(() => {
      this.scrollToSignatureField(stop.index);
      if (openSignaturePad) this.openMobileBottomSheet();
    }, 250);
  }

  /** What the phone's main button does: sign the signature the signer is on, move on through the form, or finish once
   *  every signature is done and no field is left further down. */
  mobilePrimaryAction(): 'finish' | 'sign' | 'next' {
    if (this.signingStops.length === 0) {
      if (this.allSignaturesCompleted()) return 'finish';
      return this.hasSignatureForStep(this.currentSigIndex) ? 'next' : 'sign';
    }
    const allSigned = this.allSignaturesCompleted();
    if (this.activeFillFieldName === null && !this.hasSignatureForStep(this.currentSigIndex) && !allSigned) return 'sign';
    // The phone has this one button. At the end of the form it must offer Finish even while a required
    // field further up is empty: Finish warns, names the pages, and offers "Go back and fill in" or
    // "Submit anyway" (IOHB-1779: warn, never block). Counting those fields here would turn the button
    // into a Next that cycles through the form and never offers a way to submit. The Next button on a
    // computer still takes the signer back to them.
    const target = this.nextStopTarget(false);
    if (allSigned && target?.kind !== 'fill') return 'finish';
    return target ? 'next' : 'sign';
  }

  nextStopLabel(): string {
    return this.nextStopTarget()?.kind === 'fill' ? 'Next Field' : 'Next Signature';
  }

  /** After consent: takes the signer to the first field or signature of theirs in the form. */
  scrollToFirstStop() {
    const first = this.signingStops[0];
    if (!first) {
      this.scrollToSignatureField(this.currentSigIndex);
      return;
    }
    if (first.kind === 'fill') {
      this.setActiveFillField(first.name);
      this.scrollToFillField(first.name);
    } else {
      this.setActiveFillField(null);
      if (first.index !== this.currentSigIndex) {
        this.currentSigIndex = first.index;
        this.loadSignatureForStep(first.index);
      }
      this.scrollToSignatureField(first.index);
    }
    this.cdr.detectChanges();
  }

  allSignaturesCompleted(): boolean {
    if (!this.sigFieldsList || this.sigFieldsList.length === 0) return false;
    // Every signer signs at least once; a signature spot the agency marked optional may stay unsigned
    // (IOHB-1779).
    if (!this.sigFieldsList.some(s => !!s.signatureImage)) return false;
    return this.sigFieldsList.every(s => !!s.signatureImage || this.isOptionalSignatureField(s.name));
  }

  /** True when the agency marked this signature field optional when preparing the document. */
  isOptionalSignatureField(fieldName: string): boolean {
    const ld = this.linkDetails || {};
    for (let i = 1; i <= 10; i++) {
      const suffix = i === 1 ? '' : String(i);
      if (fieldName && ld[`signatureFieldName${suffix}`] === fieldName && ld[`signatureFieldName${suffix}Optional`] === true) {
        return true;
      }
    }
    return false;
  }

  /** The recipient's mapped date fields to auto-date on the sealed PDF. Dates are mapped to the
   *  PERSON (not paired to a specific signature slot), so ALL of this recipient's mapped dates fill
   *  once the recipient has actually signed — and never before. A date is never filled for a signer
   *  who hasn't signed, so on a multi-signer form one person signing no longer dates the others.
   *  Un-mapped date fields are left blank (there is no proximity/auto fallback any more). */
  dateFieldsToFill(linkDetails: any, signedSigNames: Set<string>): string[] {
    const ld = linkDetails || {};
    const sigNames = [
      ld.signatureFieldName, ld.signatureFieldName2, ld.signatureFieldName3, ld.signatureFieldName4,
      ld.signatureFieldName5, ld.signatureFieldName6, ld.signatureFieldName7, ld.signatureFieldName8,
      ld.signatureFieldName9, ld.signatureFieldName10,
    ].filter(Boolean);
    const dateNames = [
      ld.dateFieldName, ld.dateFieldName2, ld.dateFieldName3, ld.dateFieldName4, ld.dateFieldName5,
      ld.dateFieldName6, ld.dateFieldName7, ld.dateFieldName8, ld.dateFieldName9, ld.dateFieldName10,
    ].filter(Boolean);
    const recipientSigned = sigNames.some((s) => signedSigNames.has(s));
    return recipientSigned ? dateNames : [];
  }

  private _typedMeasureCtx: CanvasRenderingContext2D | null = null;
  /** Font size (px) for a typed signature so it FITS inside its box width. Starts from the
   *  height-based size and shrinks it when a long name would overflow, so the signature always
   *  stays within the box (and reads centred with room to spare) instead of spilling past the edges. */
  typedSigFontSize(sigBox: any): number {
    const base = Math.max(8, ((sigBox?.displayHeight || 20) - 10) * 0.75);
    const text = (sigBox?.signatureImage || '').toString();
    // Box padding (~8px) + the text div's own padding (~10px) eat into the usable width — allow for
    // both so the shrunk text fully fits inside the visible box instead of clipping at the edge.
    const maxWidth = (sigBox?.displayWidth || 0) - 20;
    if (!text || maxWidth <= 0) return base;
    if (!this._typedMeasureCtx) {
      this._typedMeasureCtx = document.createElement('canvas').getContext('2d');
    }
    if (!this._typedMeasureCtx) return base;
    this._typedMeasureCtx.font = `${base}px "Caveat", cursive, sans-serif`;
    const width = this._typedMeasureCtx.measureText(text).width;
    if (width <= maxWidth) return base;
    return Math.max(8, Math.floor(base * (maxWidth / width)));
  }

  onTypedSignatureChange(val: string) {
    this.updateActiveSignature();
  }

  clearCanvas(skipUpdate: boolean = false) {
    if (!this.sigCanvas || !this.ctx) return;
    const canvas = this.sigCanvas.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.drawnSignature = null;
    this.drawnSignatureFull = null;
    if (!skipUpdate) {
      this.updateActiveSignature();
    }
  }

  saveCanvasState() {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;
    this.drawnSignatureFull = canvas.toDataURL('image/png');
    this.drawnSignature = this.trimCanvas(canvas);
    this.updateActiveSignature();
  }

  trimCanvas(canvas: HTMLCanvasElement): string {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas.toDataURL('image/png');

    const width = canvas.width;
    const height = canvas.height;
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let found = false;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          found = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!found) {
      return canvas.toDataURL('image/png');
    }

    const padding = 5;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(width - 1, maxX + padding);
    maxY = Math.min(height - 1, maxY + padding);

    const cropWidth = maxX - minX + 1;
    const cropHeight = maxY - minY + 1;

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cropWidth;
    cropCanvas.height = cropHeight;
    const cropCtx = cropCanvas.getContext('2d');

    if (cropCtx) {
      cropCtx.drawImage(
        canvas,
        minX, minY, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );
      return cropCanvas.toDataURL('image/png');
    }

    return canvas.toDataURL('image/png');
  }

  onSignatureFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedSignature = e.target.result;
        this.updateActiveSignature();
      };
      reader.readAsDataURL(file);
    }
  }

  clearUploadedSignature() {
    this.uploadedSignature = null;
    this.updateActiveSignature();
  }

  hasSignature(): boolean {
    if (this.signatureMode === 'draw') {
      return !!this.drawnSignature;
    } else if (this.signatureMode === 'type') {
      return !!this.typedSignature && this.typedSignature.trim() !== '';
    } else if (this.signatureMode === 'upload') {
      return !!this.uploadedSignature;
    }
    return false;
  }

  getSignatureImage(): string | null {
    if (this.signatureMode === 'draw') {
      return this.drawnSignature;
    } else if (this.signatureMode === 'type') {
      return this.typedSignature;
    } else if (this.signatureMode === 'upload') {
      return this.uploadedSignature;
    }
    return null;
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly esocService: EsocService,
    private readonly messageHandlerService: MessageHandlerService,
    private readonly datePipe: DatePipe,
    private readonly utilService: UtilService,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef
  ) {}

  zoomIn() {
    if (this.zoomScale < 3) {
      this.zoomFromCenter(Math.min(3, this.zoomScale + 0.2));
    }
  }

  zoomOut() {
    if (this.zoomScale > 0.6) {
      this.zoomFromCenter(Math.max(0.6, this.zoomScale - 0.2));
    }
  }

  /** Re-render at a new scale while keeping the current viewport centre fixed,
   *  so +/- zooms from the middle instead of the top-left corner (IOHB-1632). */
  private zoomFromCenter(newScale: number) {
    const el = this.pdfViewerScroll?.nativeElement;
    const cx = el && el.scrollWidth ? (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth : 0.5;
    const cy = el && el.scrollHeight ? (el.scrollTop + el.clientHeight / 2) / el.scrollHeight : 0.5;
    this.zoomScale = newScale;
    this.renderPages();
    setTimeout(() => {
      const e = this.pdfViewerScroll?.nativeElement;
      if (e) {
        e.scrollLeft = Math.max(0, cx * e.scrollWidth - e.clientWidth / 2);
        e.scrollTop = Math.max(0, cy * e.scrollHeight - e.clientHeight / 2);
      }
    }, 400);
  }

  resetZoom() {
    if (this.zoomScale !== 1) {
      this.zoomScale = 1;
      this.renderPages();
    }
  }

  startDragZoom(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.closest('button')) return;

    this.isDraggingZoom = true;
    this.zoomDragStartX = event.clientX;
    this.zoomDragStartY = event.clientY;
    this.zoomPanelStartX = this.zoomX;
    this.zoomPanelStartY = this.zoomY;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isDraggingZoom) return;
      const dx = this.zoomDragStartX - e.clientX;
      const dy = e.clientY - this.zoomDragStartY;

      this.zoomX = Math.max(0, this.zoomPanelStartX + dx);
      this.zoomY = Math.max(0, this.zoomPanelStartY + dy);
    };

    const onMouseUp = () => {
      this.isDraggingZoom = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  startDragZoomTouch(event: TouchEvent) {
    if (event.touches.length === 0) return;
    const target = event.target as HTMLElement;
    if (target.closest('button')) return;

    this.isDraggingZoom = true;
    const touch = event.touches[0];
    this.zoomDragStartX = touch.clientX;
    this.zoomDragStartY = touch.clientY;
    this.zoomPanelStartX = this.zoomX;
    this.zoomPanelStartY = this.zoomY;

    const onTouchMove = (e: TouchEvent) => {
      if (!this.isDraggingZoom || e.touches.length === 0) return;
      const t = e.touches[0];
      const dx = this.zoomDragStartX - t.clientX;
      const dy = t.clientY - this.zoomDragStartY;

      this.zoomX = Math.max(0, this.zoomPanelStartX + dx);
      this.zoomY = Math.max(0, this.zoomPanelStartY + dy);
      e.preventDefault();
    };

    const onTouchEnd = () => {
      this.isDraggingZoom = false;
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  }

  isPanning = false;
  panStartX = 0;
  panStartY = 0;
  panScrollLeft = 0;
  panScrollTop = 0;

  onScrollMouseDown(event: MouseEvent) {
    if (event.button !== 0) return;

    const target = event.target as HTMLElement;
    if (
      target.closest('.draggable-signature-box') ||
      target.closest('.pdf-zoom-controls') ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea')
    ) {
      return;
    }

    this.isPanning = true;
    this.panStartX = event.clientX;
    this.panStartY = event.clientY;

    const scrollEl = this.pdfViewerScroll.nativeElement;
    this.panScrollLeft = scrollEl.scrollLeft;
    this.panScrollTop = scrollEl.scrollTop;

    scrollEl.style.cursor = 'grabbing';
    scrollEl.style.userSelect = 'none';

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isPanning) return;
      const dx = e.clientX - this.panStartX;
      const dy = e.clientY - this.panStartY;
      scrollEl.scrollLeft = this.panScrollLeft - dx;
      scrollEl.scrollTop = this.panScrollTop - dy;
    };

    const onMouseUp = () => {
      this.isPanning = false;
      scrollEl.style.cursor = '';
      scrollEl.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  setupPinchZoom() {
    const el = this.pdfViewerScroll?.nativeElement;
    if (!el) return;

    el.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length === 2) {
        this.isPinching = true;
        this.initialTouchDistance = this.getTouchDistance(e);
        this.initialZoomScale = this.zoomScale;
        // Anchor the zoom at the pinch midpoint (fingers), in the pages container's
        // local pre-transform coordinates, instead of top-center (IOHB-1632).
        const pagesContainer = el.querySelector('.pdf-pages-container') as HTMLElement;
        if (pagesContainer) {
          const r = pagesContainer.getBoundingClientRect();
          this.pinchOriginX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left;
          this.pinchOriginY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
        }
      }
    }, { passive: true });

    el.addEventListener('touchmove', (e: TouchEvent) => {
      if (this.isPinching && e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = this.getTouchDistance(e);
        if (this.initialTouchDistance > 0) {
          const factor = currentDistance / this.initialTouchDistance;
          let newScale = this.initialZoomScale * factor;
          newScale = Math.max(0.5, Math.min(3.0, newScale));
          this.applyVisualZoom(newScale);
        }
      }
    }, { passive: false });

    el.addEventListener('touchend', (e: TouchEvent) => {
      if (this.isPinching) {
        this.isPinching = false;
        this.resetVisualZoom();
        if (Math.abs(this.zoomScale - this.tempZoomScale) > 0.02) {
          this.zoomScale = this.tempZoomScale;
          this.renderPages();
        }
      }
    }, { passive: true });
  }

  private getTouchDistance(e: TouchEvent): number {
    const t1 = e.touches[0];
    const t2 = e.touches[1];
    return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  }

  private applyVisualZoom(scale: number) {
    this.tempZoomScale = scale;
    const pagesContainer = this.pdfViewerScroll?.nativeElement.querySelector('.pdf-pages-container') as HTMLElement;
    if (pagesContainer) {
      pagesContainer.style.transform = `scale(${scale / this.zoomScale})`;
      pagesContainer.style.transformOrigin =
        this.pinchOriginX != null ? `${this.pinchOriginX}px ${this.pinchOriginY}px` : 'top center';
    }
  }

  private resetVisualZoom() {
    const pagesContainer = this.pdfViewerScroll?.nativeElement.querySelector('.pdf-pages-container') as HTMLElement;
    if (pagesContainer) {
      pagesContainer.style.transform = '';
      pagesContainer.style.transformOrigin = '';
    }
    this.pinchOriginX = null;
    this.pinchOriginY = null;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    // The pages are drawn to the window's width, so only a change of width needs them drawn again. A phone's keyboard
    // and its collapsing address bar change the height alone, and fire this event too: drawing the pages again then
    // replaces the fill-in fields on them, which takes the cursor off the field the signer has just tapped and closes
    // the keyboard on the spot (IOHB-2000).
    const widthChanged = window.innerWidth !== this.innerWidth;
    this.innerWidth = window.innerWidth;
    if (this.pdfDoc && widthChanged) {
      this.renderPages();
    }
    // Rotating / resizing changes the open sheet's height — re-measure so the reserved PDF-scroll
    // room stays correct (otherwise the document bottom can hide behind the drawer again). IOHB-1676.
    if (this.showMobileBottomSheet) {
      setTimeout(() => this.updateMobileSheetPad(), 120);
    }
  }

  syncTimeWithServer() {
    this.esocService.getServerTime().subscribe({
      next: (res) => {
        if (res && res.timestamp) {
          const serverTime = new Date(res.timestamp).getTime();
          const clientTime = Date.now();
          this.timeOffset = serverTime - clientTime;
          this.now = new Date(Date.now() + this.timeOffset);
        }
        this.startTimeInterval();
      },
      error: (err) => {
        console.error('Failed to sync time with server', err);
        this.startTimeInterval();
      }
    });
  }

  startTimeInterval() {
    if (this.timeIntervalId) {
      clearInterval(this.timeIntervalId);
    }
    this.timeIntervalId = setInterval(() => {
      this.now = new Date(Date.now() + this.timeOffset);
    }, 1000);
  }

  onConsentChange() {
    if (this.consent && this.code) {
      // The session token proves the date-of-birth check was passed; the server requires it for consent
      // and records the consent wording itself.
      this.esocService.logEsignAuditEvent({
        code: this.code,
        eventType: 'CONSENT_GIVEN',
        token: this.sessionToken,
      }).subscribe({
        next: () => {
          console.log('Consent event logged successfully');
        },
        error: (err) => {
          console.error('Failed to log consent event', err);
        }
      });
    }

    if (this.consent && this.isMobile) {
      this.currentSigIndex = 0;
      this.loadSignatureForStep(0);
      if (this.sigFieldsList.length > 0 && !this.hasScrolledInitially) {
        this.hasScrolledInitially = true;
        setTimeout(() => {
          this.scrollToFirstStop();
        }, 300);
      }
    }
  }

  acceptConsent() {
    this.consent = true;
    this.showConsentOverlay = false;
    this.onConsentChange();
    this.currentSigIndex = 0;
    this.loadSignatureForStep(0);
    if (this.sigFieldsList.length > 0 && !this.hasScrolledInitially) {
      this.hasScrolledInitially = true;
      setTimeout(() => {
        this.scrollToFirstStop();
      }, 350);
    }
  }

  declineConsent() {
    // A refusal is evidence too. Recorded before the page resets, while the verified session is in hand.
    if (this.code && this.sessionToken) {
      this.esocService.logEsignAuditEvent({
        code: this.code,
        eventType: 'CONSENT_DECLINED',
        token: this.sessionToken,
      }).subscribe({
        error: (err) => console.error('Failed to log consent declined event', err),
      });
    }
    this.consent = false;
    this.isVerified = false;
    this.dob = null;
    this.showConsentOverlay = true;
    this.resetDisclosureGate();
    this.messageHandlerService.generateToastMessage('info', 'Decline', 'You have declined the consent. You will not be able to sign the document.');
  }

  // Desktop consent gate: the checkbox + "Agree & Continue" stay disabled until the disclosure has
  // been scrolled to the bottom, so a signer can't consent without reviewing it.
  disclosureScrolledToEnd = false;

  onDisclosureScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      this.disclosureScrolledToEnd = true;
    }
  }

  /** Reset the scroll gate when the consent modal opens, and if the disclosure happens to fit
   *  without scrolling, count it as read immediately so it can never be stuck. */
  private resetDisclosureGate() {
    this.disclosureScrolledToEnd = false;
    setTimeout(() => {
      const box = document.querySelector('.consent-modal-card .disclosure-box') as HTMLElement;
      if (box && box.scrollHeight <= box.clientHeight + 8) this.disclosureScrolledToEnd = true;
    }, 300);
  }

  /** First name only for the "Signing as" / recipient labels (no last name shown; contact info is
   *  never exposed to the client). */
  recipientFirstName(): string {
    const full = (this.linkDetails?.recipientName || this.linkDetails?.patientName || '').trim();
    return full ? full.split(/\s+/)[0] : '--';
  }

  /** Warm the Caveat handwriting font as soon as the signing page loads, so it is ready well before
   *  the Type step. Without this the first typed-signature render briefly shows a fallback font and
   *  then visibly swaps to Caveat. Fire-and-forget; on failure the @font-face's bundled woff2 is used. */
  private preloadCaveatFont(): void {
    try {
      const fonts = (document as any).fonts;
      if (fonts?.load) {
        fonts.load('700 32px "Caveat"').catch(() => {});
        fonts.load('700 96px "Caveat"').catch(() => {});
      }
    } catch { /* no-op */ }
  }

  ngOnInit(): void {
    this.requestGeolocation();
    this.syncTimeWithServer();
    this.preloadCaveatFont();
    const querySub = this.activatedRoute.queryParams.subscribe(params => {
      if (params['code']) {
        let codeVal = params['code'].replace(/ /g, '+');
        // Clean leading/trailing quotes in case of copy-paste errors from console logs
        codeVal = codeVal.replace(/^["']|["']$/g, '');
        this.code = codeVal;
        this.loadLinkDetails();
      } else {
        this.messageHandlerService.generateToastMessage('error', 'Error', 'No verification code provided.');
      }
    });
    this.subscriptions.push(querySub);
  }

  loadLinkDetails(signedFileDetails?: any) {
    this.loading = true;
    const linkSub = this.esocService.getEsignLinkDetails(this.code).subscribe({
      next: (res) => {
        this.loading = false;
        if (res?.success) {
          this.linkDetails = {
            ...(this.linkDetails || {}),
            ...res.data,
            ...(signedFileDetails || {})
          };
          this.linkExpired = res.data.expired || res.data.status === 'expired';
          this.isSigned = res.data.status === 'signed';
          if (this.isVerified && this.linkDetails.fileLocation) {
            this.fetchPdfUrl(this.linkDetails.fileLocation);
          }
        } else {
          this.messageHandlerService.generateToastMessage('error', 'Error', res?.message || 'Failed to load link details');
          this.linkExpired = true;
        }
      },
      error: (err) => {
        this.loading = false;
        // The server says why when it refuses a link - withdrawn, replaced - and that tells the
        // recipient what to do next. The fixed wording is for a failure that comes with no reason.
        this.messageHandlerService.generateToastMessage(
          'error',
          'Error',
          err?.error?.message || 'Failed to retrieve signature link status.',
        );
        this.linkExpired = true;
      }
    });
    this.subscriptions.push(linkSub);
  }

  formatDobDate(dob) {
    let formattedDate = this.utilService.toLocal(dob);
    return this.datePipe.transform(formattedDate, 'MM/dd/yyyy');
  }

  verifyDob() {
    if (!this.dob) {
      this.messageHandlerService.generateToastMessage('error', 'Error', "Please enter the patient's date of birth.");
      return;
    }

    this.loading = true;
    const formattedDob = this.formatDobDate(this.dob);

    const dobSub = this.esocService.verifyEsignDob({ code: this.code, dob: formattedDob }).subscribe({
      next: (res) => {
        this.loading = false;
        if (res?.success && res.match) {
          this.isVerified = true;
          this.showConsentOverlay = true;
          this.resetDisclosureGate();
          this.sessionToken = res.token;
          if (this.linkDetails) {
            this.linkDetails.fileLocation = res.fileLocation;
            this.linkDetails.patientEmail = res.patientEmail;
            this.linkDetails.patientPhone = res.patientPhone;
            this.linkDetails.patientName = res.patientName || res.recipientName;
            this.linkDetails.recipientName = res.recipientName;
            this.linkDetails.consentText = res.consentText;
            this.linkDetails.esignTemplatesList = res.esignTemplatesList;
            this.linkDetails.signatureFieldName = res.signatureFieldName;
            this.linkDetails.signatureFieldName2 = res.signatureFieldName2;
            this.linkDetails.signatureFieldName3 = res.signatureFieldName3;
            this.linkDetails.signatureFieldName4 = res.signatureFieldName4;
            this.linkDetails.signatureFieldName5 = res.signatureFieldName5;
            this.linkDetails.signatureFieldName6 = res.signatureFieldName6;
            this.linkDetails.signatureFieldName7 = res.signatureFieldName7;
            this.linkDetails.signatureFieldName8 = res.signatureFieldName8;
            this.linkDetails.signatureFieldName9 = res.signatureFieldName9;
            this.linkDetails.signatureFieldName10 = res.signatureFieldName10;
            this.linkDetails.dateFieldName = res.dateFieldName;
            this.linkDetails.dateFieldName2 = res.dateFieldName2;
            this.linkDetails.dateFieldName3 = res.dateFieldName3;
            this.linkDetails.dateFieldName4 = res.dateFieldName4;
            this.linkDetails.dateFieldName5 = res.dateFieldName5;
            this.linkDetails.dateFieldName6 = res.dateFieldName6;
            this.linkDetails.dateFieldName7 = res.dateFieldName7;
            this.linkDetails.dateFieldName8 = res.dateFieldName8;
            this.linkDetails.dateFieldName9 = res.dateFieldName9;
            this.linkDetails.dateFieldName10 = res.dateFieldName10;
            // Which signature slots are optional, and the fields this signer fills in (IOHB-1779).
            for (let i = 1; i <= 10; i++) {
              const optionalKey = `signatureFieldName${i === 1 ? '' : i}Optional`;
              this.linkDetails[optionalKey] = res[optionalKey] === true;
            }
            this.linkDetails.fillFields = Array.isArray(res.fillFields) ? res.fillFields : [];

            // Recipient contact shown under "Sign Here".
            // Email arrives as patientEmail (may be comma-joined for a patient with
            // multiple addresses) -> show the first only. Phone arrives as recipientPhone.
            this.linkDetails.recipientEmail = (res.patientEmail || '')
              .split(',')[0]
              .trim();
            this.linkDetails.recipientPhone = (res.recipientPhone || '').trim();

            // Start by viewing/rendering the first template if configured
            if (this.linkDetails.esignTemplatesList && this.linkDetails.esignTemplatesList.length > 0) {
              const firstTmpl = this.linkDetails.esignTemplatesList[0];
              this.fetchPdfUrl(firstTmpl.fileLocation);
            } else if (this.linkDetails.fileLocation) {
              this.fetchPdfUrl(this.linkDetails.fileLocation);
            }
          }
        } else {
          if (res?.locked) {
            this.dobLocked = true;
            this.dobLockMessage = res.message || 'This link is temporarily locked due to too many incorrect attempts. Please try again later.';
          }
          this.messageHandlerService.generateToastMessage('error', 'Error', res?.message || DOB_NOT_MATCH);
        }
      },
      error: (err) => {
        this.loading = false;
        this.messageHandlerService.generateToastMessage('error', 'Error', err?.error?.message || 'Verification failed. Please try again.');
      }
    });
    this.subscriptions.push(dobSub);
  }

  extractKey(fileLocation: string): string {
    if (!fileLocation) return '';
    const hosPrefix = fileLocation.includes('hos-addendum/') ? 'hos-addendum/' : '';
    const companyMrnRegex = /\/?([a-f0-9]{24}|unknown)\/([a-f0-9]{32})\//i;
    const companyMrnMatch = fileLocation.match(companyMrnRegex);
    if (companyMrnMatch) {
      const index = fileLocation.indexOf(companyMrnMatch[1]);
      let key = fileLocation.substring(index);
      const qIndex = key.indexOf('?');
      if (qIndex > -1) {
        key = key.substring(0, qIndex);
      }
      return `${hosPrefix}${key}`;
    }
    const md5Regex = /([a-f0-9]{32})\//i;
    const match = fileLocation.match(md5Regex);
    if (match) {
      const index = fileLocation.indexOf(match[0]);
      let key = fileLocation.substring(index);
      const qIndex = key.indexOf('?');
      if (qIndex > -1) {
        key = key.substring(0, qIndex);
      }
      return `${hosPrefix}${key}`;
    } else {
      let key = fileLocation;
      const lastSlash = fileLocation.lastIndexOf('/');
      if (lastSlash > -1) {
        key = fileLocation.substring(lastSlash + 1);
      }
      const qIndex = key.indexOf('?');
      if (qIndex > -1) {
        key = key.substring(0, qIndex);
      }
      return `${hosPrefix}${key}`;
    }
  }

  fetchPdfUrl(fileLocation: string) {
    if (!fileLocation) return;

    this.zoomScale = 1.0;

    const key = this.extractKey(fileLocation);

    const baseUrl = environment.endPoints.esignStreamUrl;
    let streamUrl = `${baseUrl}/${encodeURIComponent(key)}?code=${encodeURIComponent(this.code)}`;
    if (this.sessionToken) {
      streamUrl += `&token=${encodeURIComponent(this.sessionToken)}`;
    }

    // Only the fixed, environment-configured baseUrl is trusted for sanitization bypass;
    // key/code/token are user-influenced but URI-encoded and cannot escape the query string.
    if (!streamUrl.startsWith(baseUrl)) {
      console.error('Refusing to load PDF: stream URL does not match trusted esign endpoint.');
      return;
    }

    this.rawPdfUrl = streamUrl;
    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(streamUrl);
    this.loadPdfWithJs(streamUrl);
  }

  loadPdfWithJs(url: string) {
    const pdfjsLib = (window as any).pdfjsLib;
    if (!pdfjsLib) {
      console.error('PDF.js not loaded');
      return;
    }
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;

    pdfjsLib.getDocument(url).promise.then(
      (pdf) => {
        this.pdfDoc = pdf;
        this.numPages = pdf.numPages;
        this.pages = Array.from({ length: this.numPages }, (_, i) => i + 1);
        this.renderPages();

        // Retrieve the PDF data bytes directly from PDF.js
        pdf.getData().then(async (uint8Array) => {
          const PDFLib = (window as any).PDFLib;
          if (!PDFLib) {
            console.error('PDFLib not loaded in window context.');
            return;
          }

          try {
            //console.log('Successfully fetched PDF bytes via pdf.getData()');
            const pdfDocLib = await PDFLib.PDFDocument.load(uint8Array, { ignoreEncryption: true });
            const form = pdfDocLib.getForm();
            const fields = form.getFields();

            console.log(`PDF Form Fields found in esign-verification (${fields.length} fields):`, fields.map(f => ({
              name: f.getName(),
              type: f.constructor.name,
              value: typeof (f as any).getText === 'function' ? (f as any).getText() : undefined
            })));

            // Exclude date/time fields to prevent snapping over the Date input instead of Signature
            const isDateRelated = (fieldName: string) => {
              const lower = fieldName.toLowerCase();
              return lower.includes('date') || lower.includes('time') || lower.includes('day') || lower.includes('dt');
            };

             const isPatient = this.linkDetails?.isPatient === true;

              const mappedFieldNames = [
                this.linkDetails?.signatureFieldName,
                this.linkDetails?.signatureFieldName2,
                this.linkDetails?.signatureFieldName3,
                this.linkDetails?.signatureFieldName4,
                this.linkDetails?.signatureFieldName5,
                this.linkDetails?.signatureFieldName6,
                this.linkDetails?.signatureFieldName7,
                this.linkDetails?.signatureFieldName8,
                this.linkDetails?.signatureFieldName9,
                this.linkDetails?.signatureFieldName10
              ].filter(Boolean);

             console.log("ESIGN_DEBUG: mappedFieldNames =", mappedFieldNames);

             this.sigFieldsList = [];

             if (mappedFieldNames.length > 0) {
               mappedFieldNames.forEach(fieldName => {
                 const f = fields.find(field => field.getName() === fieldName);
                 console.log(`ESIGN_DEBUG: searching for "${fieldName}", found =`, !!f);
                 if (f) {
                   const widgets = f.acroField.getWidgets();
                   console.log(`ESIGN_DEBUG: widgets count for "${fieldName}" =`, widgets.length);
                   for (let wIdx = 0; wIdx < widgets.length; wIdx++) {
                     const widget = widgets[wIdx];
                     const rect = widget.getRectangle();
                     const pages = pdfDocLib.getPages();
                     let pageNumber = 1;

                      // Check P (Page) direct property in the widget dictionary first
                      const pageRef = widget.dict ? widget.dict.get(PDFLib.PDFName.of('P')) : undefined;
                      if (pageRef) {
                        const matchPageIdx = pages.findIndex(p => p.ref.objectNumber === pageRef.objectNumber);
                        if (matchPageIdx > -1) {
                          pageNumber = matchPageIdx + 1;
                        }
                      } else {
                       // Fallback: search annotations array on each page
                       const widgetRef = widget.ref || f.ref || f.acroField.ref;
                       if (widgetRef) {
                         for (let pIdx = 0; pIdx < pages.length; pIdx++) {
                           const page = pages[pIdx];
                           const annots = page.node.Annots();
                           if (annots) {
                             for (let aIdx = 0; aIdx < annots.size(); aIdx++) {
                               const annotRef = annots.get(aIdx);
                               if (annotRef && annotRef.objectNumber === widgetRef.objectNumber) {
                                 pageNumber = pIdx + 1;
                                 break;
                               }
                             }
                           }
                         }
                       }
                     }

                     this.sigFieldsList.push({
                       name: f.getName(),
                       pageNumber,
                       pdfX: rect.x,
                       pdfY: rect.y,
                       pdfWidth: rect.width,
                       pdfHeight: rect.height,
                       displayX: 50,
                       displayY: 50,
                       displayWidth: 150,
                       displayHeight: 50
                     });
                   }
                 }
               });
             }

            if (this.sigFieldsList.length === 0) {
              let sigField = fields.find(f => {
                const name = f.getName().toLowerCase();
                const isSigType = f instanceof PDFLib.PDFSignature;
                const matchesRole = isPatient
                  ? (name.includes('patient') || name.includes('client') || name.includes('pat'))
                  : (name.includes('patient') || name.includes('provider') || name.includes('caregiver') || name.includes('witness') || name.includes('doctor') || name.includes('nurse') || name.includes('representative') || name.includes('user'));

                const isSignatureField = (name.includes('signature') || name.includes('sign') || name.includes('sig')) && !isDateRelated(name);

                return (matchesRole && isSignatureField) || (isSigType && matchesRole);
              });

              if (!sigField) {
                sigField = fields.find(f => {
                  const name = f.getName().toLowerCase();
                  const isSigType = f instanceof PDFLib.PDFSignature;
                  const matchesOppositeRole = !isPatient
                    ? (name.includes('patient') || name.includes('client') || name.includes('pat'))
                    : (name.includes('patient') || name.includes('provider') || name.includes('caregiver') || name.includes('witness') || name.includes('doctor') || name.includes('nurse') || name.includes('representative') || name.includes('user'));

                  const isSignatureField = (name.includes('signature') || name.includes('sign') || name.includes('sig')) && !isDateRelated(name);

                  return (matchesOppositeRole && isSignatureField) || (isSigType && matchesOppositeRole);
                });
              }

              if (!sigField) {
                sigField = fields.find(f => {
                  const name = f.getName().toLowerCase();
                  return (name.includes('signature') || name.includes('sign') || name.includes('sig')) && !isDateRelated(name);
                });
              }

              if (!sigField) {
                sigField = fields.find(f => f instanceof PDFLib.PDFSignature);
              }

              if (!sigField) {
                sigField = fields.find(f => {
                  const name = f.getName().toLowerCase();
                  const isButton = f instanceof PDFLib.PDFButton;
                  return isButton && (name.includes('image') || name.includes('img') || name.includes('pic') || name.includes('photo') || name.includes('sig') || name.includes('sign')) && !isDateRelated(name);
                });
              }

              if (!sigField) {
                sigField = fields.find(f => {
                  const name = f.getName().toLowerCase();
                  const isButton = f instanceof PDFLib.PDFButton;
                  return isButton && !isDateRelated(name);
                });
              }

              if (sigField) {
                const widgets = sigField.acroField.getWidgets();
                if (widgets.length > 0) {
                  const widget = widgets[0];
                  const rect = widget.getRectangle();
                  const pages = pdfDocLib.getPages();
                  let pageNumber = 1;
                  const widgetRef = widgets[0].ref || sigField.ref || sigField.acroField.ref;
                  if (widgetRef) {
                    for (let pIdx = 0; pIdx < pages.length; pIdx++) {
                      const page = pages[pIdx];
                      const annots = page.node.Annots();
                      if (annots) {
                        for (let aIdx = 0; aIdx < annots.size(); aIdx++) {
                          const annotRef = annots.get(aIdx);
                          if (annotRef && annotRef.objectNumber === widgetRef.objectNumber) {
                            pageNumber = pIdx + 1;
                            break;
                          }
                        }
                      }
                    }
                  }

                  this.sigFieldsList.push({
                    name: sigField.getName(),
                    pageNumber,
                    pdfX: rect.x,
                    pdfY: rect.y,
                    pdfWidth: rect.width,
                    pdfHeight: rect.height,
                    displayX: 50,
                    displayY: 50,
                    displayWidth: 150,
                    displayHeight: 50
                  });
                }
              }
            }

            if (this.sigFieldsList.length === 0) {
              this.sigFieldsList.push({
                name: 'Default',
                pageNumber: this.numPages || 1,
                pdfX: 50,
                pdfY: 50,
                pdfWidth: 150,
                pdfHeight: 50,
                displayX: 50,
                displayY: 50,
                displayWidth: 150,
                displayHeight: 50
              });
            }

            // Set sigFieldInfo as the first element for backward-compatibility
            this.sigFieldInfo = this.sigFieldsList[0];

            this.positionSignatureOnField();
          } catch (err) {
            console.error('Error loading PDF with pdf-lib:', err);
          }
        }).catch(err => {
          console.error('Failed to get PDF data from PDFDocumentProxy:', err);
        });
      },
      (error) => {
        console.error('Error loading PDF with PDF.js:', error);
      }
    );
  }

  renderPages() {
    this.resetVisualZoom();
    // Field labels are placed again once the pages are drawn, so none show in an old position meanwhile (IOHB-1886).
    this.fillFieldTags = [];
    setTimeout(() => {
      const renderPromises: Promise<any>[] = [];
      const pdfjsLib = (window as any).pdfjsLib;
      // pdf.js needs the document's field objects to tell a clicked checkbox apart from other checkboxes with the
      // same name. Without them its own change handler unticks a checkbox the signer has just ticked (IOHB-1852).
      const fieldObjectsPromise: Promise<any> = Promise.resolve()
        .then(() => (typeof this.pdfDoc?.getFieldObjects === 'function' ? this.pdfDoc.getFieldObjects() : null))
        .catch(() => null);

      for (let i = 1; i <= this.numPages; i++) {
        const p = this.pdfDoc.getPage(i).then(async (page) => {
          const canvas = document.getElementById('pdf-canvas-' + i) as HTMLCanvasElement;
          const annotationDiv = document.getElementById('pdf-annotations-' + i) as HTMLDivElement;
          if (!canvas) return;
          const context = canvas.getContext('2d');

          const viewport = page.getViewport({ scale: 1.0 });
          const container = document.querySelector('.pdf-viewer-scroll');
          const containerWidth = container ? container.clientWidth - 40 : 600;
          const scale = (containerWidth / viewport.width) * this.zoomScale;
          const scaledViewport = page.getViewport({ scale: scale });

          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;

          const renderContext = {
            canvasContext: context,
            viewport: scaledViewport
          };
          await page.render(renderContext).promise;
          // Where the page's printed text is, so labels on the signer's fields never cover it (IOHB-1886).
          this.printedText[i] = await this.printedTextBoxes(page, i, scaledViewport);

          if (annotationDiv && pdfjsLib) {
            annotationDiv.innerHTML = '';
            annotationDiv.style.setProperty('--scale-factor', scale.toString());
            const annotations = await page.getAnnotations();
            const fieldObjects = await fieldObjectsPromise;
            this.noteCheckboxExportValues(fieldObjects, annotations);

            const renderTask = pdfjsLib.AnnotationLayer.render({
              viewport: scaledViewport.clone({ dontFlip: true }),
              div: annotationDiv,
              annotations: annotations,
              page: page,
              linkService: {
                getDestinationHash: () => '#',
                getAnchorUrl: () => '#',
                navigateTo: () => {},
                // pdf.js also calls these for link annotations. Without addLinkAttributes a page containing a
                // hyperlink throws mid-render, skipping its read-only lockdown and signature positioning (IOHB-1842).
                // Left as no-ops so links stay inert and a click can never navigate away from the signing page.
                addLinkAttributes: () => {},
                goToDestination: () => {},
                executeNamedAction: () => {},
              },
              fieldObjects,
              downloadManager: null,
              imageResourcesPath: '',
              renderInteractiveForms: true
            });

            if (renderTask && renderTask.promise) {
              await renderTask.promise;
            }

            // Lock every rendered field on the signing page, except the fields this signer fills in
            // (IOHB-1779): those are unlocked, show what the signer has entered so far, and are tracked.
            const fillFieldNames = this.isSigned || this.linkExpired ? new Set<string>() : this.fillFieldNameSet();
            const annotationById = new Map<string, any>((annotations || []).map((a: any) => [String(a?.id), a]));
            const formElements = annotationDiv.querySelectorAll('input, textarea, select, button');
            formElements.forEach((el: any) => {
              const section = el.closest('section') as HTMLElement | null;
              const annotation = annotationById.get(section?.getAttribute('data-annotation-id') || '');
              const fieldName = annotation?.fieldName || el.name;
              // A field already locked in the PDF (filled by an earlier signer) is never reopened.
              if (el.tagName !== 'BUTTON' && fieldName && fillFieldNames.has(fieldName) && !annotation?.readOnly) {
                this.unlockFillField(el, section, fieldName, annotation);
                return;
              }
              el.disabled = true;
              if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
                el.readOnly = true;
              }
              el.setAttribute('disabled', 'true');
              el.setAttribute('readonly', 'true');
              el.setAttribute('tabindex', '-1');
              el.style.pointerEvents = 'none';
              el.style.cursor = 'default';
            });
          }
        });
        renderPromises.push(p);
      }

      Promise.all(renderPromises).then(() => {
        this.positionSignatureOnField();
      }).catch(err => {
        console.error('Error rendering PDF pages:', err);
      });
    }, 100);
  }

  positionSignatureOnField() {
    if (this.sigFieldsList.length === 0 || !this.pdfDoc) return;

    setTimeout(() => {
      let resolvedCount = 0;
      this.sigFieldsList.forEach(sigBox => {
        const pageNum = sigBox.pageNumber;
        const canvas = document.getElementById('pdf-canvas-' + pageNum) as HTMLCanvasElement;
        const annotationDiv = document.getElementById('pdf-annotations-' + pageNum) as HTMLDivElement;
        if (!canvas) {
          console.warn(`Canvas pdf-canvas-${pageNum} not found in DOM yet.`);
          resolvedCount++;
          if (resolvedCount === this.sigFieldsList.length) {
            this.onFinishPositioning();
          }
          return;
        }

        this.pdfDoc.getPage(pageNum).then((page) => {
          const viewport = page.getViewport({ scale: 1.0 });
          const scale = canvas.clientWidth / viewport.width;

          if (sigBox.normalizedX !== undefined && sigBox.normalizedY !== undefined && sigBox.normalizedWidth !== undefined && sigBox.normalizedHeight !== undefined) {
            sigBox.displayX = sigBox.normalizedX * canvas.clientWidth;
            sigBox.displayY = sigBox.normalizedY * canvas.clientHeight;
            sigBox.displayWidth = sigBox.normalizedWidth * canvas.clientWidth;
            sigBox.displayHeight = sigBox.normalizedHeight * canvas.clientHeight;
            this.cdr.detectChanges();
            resolvedCount++;
            if (resolvedCount === this.sigFieldsList.length) {
              this.onFinishPositioning();
            }
            return;
          }

          // Try DOM-based positioning first
          let el: HTMLElement | null = null;
          if (annotationDiv && sigBox.name) {
            const name = sigBox.name;
            el = annotationDiv.querySelector(`[name="${name}"], [id="${name}"]`) as HTMLElement;
            if (!el) {
              const allElements = annotationDiv.querySelectorAll('*');
              for (let idx = 0; idx < allElements.length; idx++) {
                const item = allElements[idx] as any;
                if (item.name === name || item.id === name || (item.getAttribute && item.getAttribute('data-annotation-id') === name)) {
                  el = item;
                  break;
                }
              }
            }
          }

          if (el) {
            const widgetRect = el.getBoundingClientRect();
            const layerRect = annotationDiv.getBoundingClientRect();

            sigBox.displayX = widgetRect.left - layerRect.left;
            sigBox.displayY = widgetRect.top - layerRect.top;

            // Get unscaled base sizes so that clamps are scale-invariant
            const unscaledHeight = widgetRect.height / this.zoomScale;
            const unscaledWidth = widgetRect.width / this.zoomScale;

            const isMobile = this.isMobile;
            const baseHeight = isMobile ? 20 : Math.max(30, Math.min(100, unscaledHeight));
            const baseWidth = isMobile ? 60 : Math.max(90, Math.min(300, unscaledWidth));

            sigBox.displayHeight = baseHeight * this.zoomScale;
            sigBox.displayWidth = baseWidth * this.zoomScale;
            this.sigHeight = Math.round(baseHeight);
            this.sigWidth = Math.round(baseWidth);
          } else {
            // Fallback to mathematical calculation
            const displayX = sigBox.pdfX * scale;
            const displayY = (viewport.height - sigBox.pdfY - sigBox.pdfHeight) * scale;
            const displayWidth = sigBox.pdfWidth * scale;
            const displayHeight = sigBox.pdfHeight * scale;

            sigBox.displayX = displayX;
            sigBox.displayY = displayY;

            const isMobile = this.isMobile;
            const baseHeight = isMobile ? 20 : Math.max(30, Math.min(100, displayHeight / this.zoomScale));
            const baseWidth = isMobile ? 60 : Math.max(90, Math.min(300, displayWidth / this.zoomScale));

            sigBox.displayHeight = baseHeight * this.zoomScale;
            sigBox.displayWidth = baseWidth * this.zoomScale;
            this.sigHeight = Math.round(baseHeight);
            this.sigWidth = Math.round(baseWidth);
          }

          // Initialize normalized coords for responsiveness
          sigBox.normalizedX = sigBox.displayX / canvas.clientWidth;
          sigBox.normalizedY = sigBox.displayY / canvas.clientHeight;
          sigBox.normalizedWidth = sigBox.displayWidth / canvas.clientWidth;
          sigBox.normalizedHeight = sigBox.displayHeight / canvas.clientHeight;

          this.cdr.detectChanges();
          resolvedCount++;
          if (resolvedCount === this.sigFieldsList.length) {
            this.onFinishPositioning();
          }
        });
      });
    }, 150);
  }

  onFinishPositioning() {
    this.updateSigningStops();
    if (this.consent && !this.hasScrolledInitially) {
      this.hasScrolledInitially = true;
      setTimeout(() => {
        this.scrollToFirstStop();
      }, 500);
    }
  }

  // IOHB-1725: signature boxes are LOCKED to their mapped field position — the signer cannot drag
  // them. Web now matches mobile, which has been locked since accidental touch-drags were moving
  // signatures. Placement is owned by the agency template, and the sealed PDF uses the field's own
  // rectangle. Clicking a box still selects that field to sign (see selectSigStep / the (click)
  // binding); only the drag-to-reposition handlers were removed.

  calculateSignaturePosition() {
    if (this.sigFieldsList.length === 0) return null;
    return this.sigFieldsList[0];
  }

  getPagesSafely(pdfDoc: any): any[] {
    const PDFLib = (window as any).PDFLib;
    if (!PDFLib) {
      throw new Error('PDF-lib is not loaded.');
    }

    // First, try standard getPages() as most PDFs (even linearized ones) load successfully
    try {
      const standardPages = pdfDoc.getPages();
      if (standardPages && standardPages.length > 0) {
        return standardPages;
      }
    } catch (e) {
      console.warn('Standard pdfDoc.getPages() failed, initiating custom page recovery:', e);
    }

    try {
      const pagesList: { ref: any; obj: any }[] = [];
      const indirectObjects = pdfDoc.context.enumerateIndirectObjects();
      for (const [ref, obj] of indirectObjects) {
        if (obj instanceof PDFLib.PDFDict) {
          const type = obj.get(PDFLib.PDFName.of('Type'));
          if (type && type.toString() === '/Page') {
            pagesList.push({ ref, obj });
          }
        }
      }
      if (pagesList.length === 0) {
        throw new Error('No page objects found in PDF document context.');
      }

      // Sort pages in order of their object number index
      pagesList.sort((a, b) => a.ref.objectNumber - b.ref.objectNumber);

      // Reconstruct the Pages directory tree using PDFPageTree (NOT PDFDict) so it supports traversal
      const kidsArray = PDFLib.PDFArray.withContext(pdfDoc.context);
      pagesList.forEach(p => {
        kidsArray.push(p.ref);
      });

      const newPagesTree = PDFLib.PDFPageTree.withContext(pdfDoc.context);
      newPagesTree.set(PDFLib.PDFName.of('Kids'), kidsArray);
      newPagesTree.set(PDFLib.PDFName.of('Count'), PDFLib.PDFNumber.of(pagesList.length));

      const newPagesRef = pdfDoc.context.register(newPagesTree);

      // Update catalog Pages reference
      pdfDoc.catalog.set(PDFLib.PDFName.of('Pages'), newPagesRef);

      // Update Parent references in each Page to point to the new Pages directory
      pagesList.forEach(p => {
        p.obj.set(PDFLib.PDFName.of('Parent'), newPagesRef);
      });

      // CRITICAL: Invalidate the internal caches on pdfDoc so standard methods use the new Pages tree
      if (pdfDoc.pageCache) {
        pdfDoc.pageCache.invalidate();
      }
      pdfDoc.pageCount = undefined;

      const recoveredPages = pdfDoc.getPages();
      console.log(`Custom page recovery successfully reconstructed Pages tree and retrieved ${recoveredPages.length} pages.`);
      return recoveredPages;
    } catch (fallbackErr: any) {
      console.error('Failed to run PDF custom page tree recovery:', fallbackErr);
      throw new Error(`Failed to recover PDF page catalog structures: ${fallbackErr.message}`);
    }
  }

  /**
   * WYSIWYG typed signatures: rasterise the typed name to a PNG using the SAME 'Caveat'
   * handwriting font the signing UI shows on screen, so the sealed PDF matches exactly what the
   * signer approved (previously the PDF substituted the built-in Times-Italic font). The font
   * stack mirrors the on-screen CSS ('Caveat', cursive), so screen and document stay consistent
   * even if the web font has not finished loading. Returns a data URL, or '' on failure so the
   * caller can fall back to drawing text.
   */
  private async renderTypedSignatureToImage(text: string): Promise<string> {
    try {
      const value = (text || '').trim();
      if (!value) return '';
      const fontSpec = '700 96px "Caveat", cursive';
      // Best-effort: ensure the handwriting font is loaded before rasterising it.
      const fontSet = (document as any).fonts;
      if (fontSet?.load) {
        try { await fontSet.load('700 96px "Caveat"'); await fontSet.ready; } catch { /* fall back to cursive */ }
      }
      const measureCtx = document.createElement('canvas').getContext('2d');
      if (!measureCtx) return '';
      measureCtx.font = fontSpec;
      const m = measureCtx.measureText(value);
      // Crop tightly to the glyph's ACTUAL bounds (ascenders/descenders + any left/right overhang)
      // rather than a fixed tall canvas. Removing the built-in whitespace makes the signature fill
      // the field (appears larger) and sit its baseline close to the verification stamp — with no
      // change to the field placement or stamp logic. Fall back to sane defaults on browsers that
      // lack the actualBoundingBox* text metrics.
      const ascent = Math.ceil(m.actualBoundingBoxAscent || 70);
      const descent = Math.ceil(m.actualBoundingBoxDescent || 28);
      const left = Math.ceil(Math.max(0, (m as any).actualBoundingBoxLeft || 0));
      const right = Math.ceil(Math.max(m.width, (m as any).actualBoundingBoxRight || m.width));
      const padX = 16, padTop = 6, padBottom = 6;
      const width = Math.max(40, left + right + padX * 2);
      const height = ascent + descent + padTop + padBottom;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#000000'; // typed signatures render in black ink, matching drawn signatures
      ctx.font = fontSpec;
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(value, padX + left, padTop + ascent);
      return canvas.toDataURL('image/png');
    } catch {
      return '';
    }
  }

  async generateSignedPdf(): Promise<ArrayBuffer> {
    try {
      if (this.sigFieldsList.length === 0) {
        throw new Error('Please place the signature on the document first.');
      }

      const PDFLib = (window as any).PDFLib;
      if (!PDFLib) throw new Error('PDF-lib is not loaded.');
      if (!this.pdfDoc || this.numPages === 0) {
        throw new Error('PDF not fully loaded yet. Please wait and try again.');
      }

      const originalBytes = await this.pdfDoc.getData();
      const pdfDocLib = await PDFLib.PDFDocument.load(originalBytes, { ignoreEncryption: true });
      const form = pdfDocLib.getForm();

      // Write this signer's fill-in fields and lock them for later signers (IOHB-1779).
      this.applyFillFieldValues(form);

      // Auto-date ONLY the date fields MAPPED to this recipient — and only once they have actually
      // signed. Dates are mapped to the person (manual mapping), so all of this recipient's mapped
      // dates fill together when they sign; an un-mapped date field is left blank (the old
      // proximity/keyword fallback has been removed) and another signer's date is never touched.
      const syncedDate = new Date(Date.now() + this.timeOffset);
      const dateStr = syncedDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
      const setDateIfEmpty = (name: string) => {
        try {
          const df = form.getField(name);
          if (df && typeof (df as any).setText === 'function') {
            const cur = (df as any).getText();
            if (!cur || cur.trim() === '') (df as any).setText(dateStr);
          }
        } catch (e) {
          console.warn(`Could not auto-fill date field ${name}:`, e);
        }
      };
      const signedSigNames = new Set((this.sigFieldsList || []).filter(b => !!b.signatureImage).map(b => b.name));
      this.dateFieldsToFill(this.linkDetails, signedSigNames).forEach(setDateIfEmpty);

      // Draw all signatures and their metadata stamps directly on the pdfDocLib pages
      for (const sigBox of this.sigFieldsList) {
        let sigImgData = sigBox.signatureImage;
        if (!sigImgData) continue;

        // WYSIWYG: a typed signature is stored as plain text and shown on screen in the 'Caveat'
        // handwriting font. Rasterise it in that SAME font so the sealed PDF matches what the
        // signer approved (instead of substituting Times-Italic). Draw/upload signatures are
        // already images and pass through unchanged.
        if (!sigImgData.startsWith('data:image/')) {
          const typedImg = await this.renderTypedSignatureToImage(sigImgData);
          if (typedImg) sigImgData = typedImg;
        }

        const targetPage = pdfDocLib.getPage(sigBox.pageNumber - 1);
        const pageW = targetPage.getWidth();
        const pageH = targetPage.getHeight();

        // PLACEMENT: default to the signature FIELD's own PDF rectangle (pdfX/pdfY/pdfWidth/
        // pdfHeight from the widget). This is reliable and independent of the on-screen render
        // scale / zoom / async DOM positioning. Only map from the on-screen display coordinates
        // when the user has actually dragged the box to a custom spot (userMoved), or when the
        // field rectangle is unknown. Previously this ALWAYS used display coords, so if the
        // async positioning hadn't settled (e.g. after a zoom re-render, or a fast submit) the
        // signature was placed at its default screen position and landed in the wrong region.
        let pdfX: number = sigBox.pdfX;
        let pdfY: number = sigBox.pdfY;
        let pdfW: number = sigBox.pdfWidth;
        let pdfH: number = sigBox.pdfHeight;
        const hasFieldRect =
          typeof pdfX === 'number' && typeof pdfY === 'number' && pdfW > 0 && pdfH > 0;

        if (!hasFieldRect || sigBox.userMoved) {
          const displayCanvas = document.getElementById('pdf-canvas-' + sigBox.pageNumber) as HTMLCanvasElement;
          if (displayCanvas) {
            const clientW = displayCanvas.clientWidth || displayCanvas.width;
            const clientH = displayCanvas.clientHeight || displayCanvas.height;
            const normX = sigBox.displayX / clientW;
            const normY = sigBox.displayY / clientH;
            const normW = sigBox.displayWidth / clientW;
            const normH = sigBox.displayHeight / clientH;
            pdfX = normX * pageW;
            pdfY = pageH - (normY + normH) * pageH;
            pdfW = normW * pageW;
            pdfH = normH * pageH;
          } else if (!hasFieldRect) {
            continue; // no field rect and no canvas → cannot resolve a position
          }
        }

        // Left edge of the actually-drawn signature, so the verification stamp can sit
        // directly beneath it. Image signatures are centred within the field, so a stamp
        // pinned to the field's own left edge would float far to the left of the signature.
        let sigStampX = pdfX;
        if (sigImgData && sigImgData.startsWith('data:image/')) {
          const base64Data = sigImgData.replace(/^data:image\/\w+;base64,/, '');
          const imgBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));

          const embeddedSig = sigImgData.includes('image/png')
            ? await pdfDocLib.embedPng(imgBytes)
            : await pdfDocLib.embedJpg(imgBytes);

          const imgWidth = embeddedSig.width;
          const imgHeight = embeddedSig.height;
          const imgAspectRatio = imgWidth / imgHeight;

          // Reserve a thin strip at the bottom of the field for the verification stamp, and
          // fit the signature ABOVE it so the two never overlap.
          const STAMP_STRIP = Math.min(7, pdfH * 0.28);
          const fitBottom = pdfY + STAMP_STRIP;
          const fitH = pdfH - STAMP_STRIP;
          const boxAspectRatio = pdfW / fitH;

          let drawW = pdfW;
          let drawH = fitH;
          let drawX = pdfX;
          let drawY = fitBottom;

          if (imgAspectRatio > boxAspectRatio) {
            drawW = pdfW;
            drawH = pdfW / imgAspectRatio;
            drawY = fitBottom + (fitH - drawH) / 2;
          } else {
            drawH = fitH;
            drawW = fitH * imgAspectRatio;
            drawX = pdfX + (pdfW - drawW) / 2;
          }

          targetPage.drawImage(embeddedSig, { x: drawX, y: drawY, width: drawW, height: drawH });
          sigStampX = drawX;

        } else if (sigImgData) {
          const font = await pdfDocLib.embedFont('Times-Italic');
          targetPage.drawText(sigImgData, {
            x: pdfX, y: pdfY + pdfH * 0.32,
            size: pdfH * 0.5,
            font,
            color: PDFLib.rgb(0, 0, 0),
          });
        }

        // Compact verification stamp, kept INSIDE the bottom strip of the signature field so
        // it stays small and overlaps neither the signature above nor the form labels below.
        // (Previously 11pt and drawn 18pt BELOW the field, which overprinted the form text.)
        const stampFont = await pdfDocLib.embedFont('Helvetica');
        const docId = this.linkDetails?._id || 'unknown';

        // Only the stamp goes under the signature - no typed name. The name was added to match the
        // hospice representative row, which the current addendum form no longer has, and at 7pt up
        // it printed across the bottom of the signature itself. Product asked for the stamp alone,
        // the way e-sign did it before (30 Sep).
        targetPage.drawText(`|io|--${docId}`, {
          x: sigStampX + 1,
          y: pdfY + 1.5,
          size: 4.5,
          font: stampFont,
          color: PDFLib.rgb(0, 0, 0), // black, matching the signature ink
        });

        // CRITICAL: Purge the widget annotations of this field from the page's /Annots list
        // so the browser's PDF viewer does not cover the drawn signature image with a blank field.
        if (sigBox.name) {
          try {
            const field = form.getField(sigBox.name);
            if (field) {
              const widgets = field.acroField.getWidgets();
              const pages = pdfDocLib.getPages();

              widgets.forEach(widget => {
                const widgetRef = widget.ref || field.ref || field.acroField.ref;
                if (widgetRef) {
                  // Search and remove the widget annotation reference from all pages' /Annots array
                  pages.forEach(p => {
                    const annots = p.node.Annots();
                    if (annots) {
                      for (let aIdx = 0; aIdx < annots.size(); aIdx++) {
                        const annotRef = annots.get(aIdx);
                        if (annotRef && annotRef.objectNumber === widgetRef.objectNumber) {
                          annots.remove(aIdx);
                          console.log(`Successfully purged widget annotation reference of ${sigBox.name} from page /Annots`);
                          aIdx--; // decrement index to account for removal
                        }
                      }
                    }
                  });
                }
              });

              // Also remove the logical field from the AcroForm
              form.removeField(field);
              console.log(`Successfully removed signed field ${sigBox.name} from AcroForm.`);
            }
          } catch (removeErr) {
            console.warn(`Could not purge field widgets for ${sigBox.name}:`, removeErr);
          }
        }
      }

      // Configure AcroForm appearances and NeedAppearances. Fields are redrawn one at a time: pdf-lib's own
      // updateFieldAppearances() stops at the first field its standard font can't draw (e.g. a Vietnamese
      // name typed into a fill-in field), which would leave every later field, such as the auto-filled date,
      // looking blank. A field that can't be redrawn keeps its value and relies on NeedAppearances (IOHB-1779).
      if (typeof form.getFields === 'function' && typeof form.getDefaultFont === 'function') {
        const appearanceFont = form.getDefaultFont();
        for (const field of form.getFields()) {
          try {
            if (field.needsAppearancesUpdate?.()) field.defaultUpdateAppearances(appearanceFont);
          } catch (appearErr) {
            console.warn(`Could not redraw field ${field.getName?.()}:`, appearErr);
            try {
              form.markFieldAsClean(field);
            } catch {
              // keep going with the other fields
            }
          }
        }
      } else if (typeof form.updateFieldAppearances === 'function') {
        try {
          form.updateFieldAppearances();
        } catch (appearErr) {
          console.warn('Could not update appearances:', appearErr);
        }
      }

      const PDFName = PDFLib.PDFName;
      const PDFBool = PDFLib.PDFBool;
      if (PDFName && PDFBool && form.acroForm && form.acroForm.dict) {
        form.acroForm.dict.set(PDFName.of('NeedAppearances'), PDFBool.True);
      }

      let signedPdfBytes: Uint8Array;
      try {
        signedPdfBytes = await pdfDocLib.save();
      } catch (saveErr: any) {
        // The standard PDF font can't draw some characters a signer may type into a fill-in field (e.g.
        // Vietnamese or Chinese names), and redrawing field appearances then fails. Save the values without
        // redrawing; NeedAppearances (set above) tells PDF viewers to draw them (IOHB-1779).
        if (!/WinAnsi cannot encode/i.test(String(saveErr?.message || saveErr))) throw saveErr;
        signedPdfBytes = await pdfDocLib.save({ updateFieldAppearances: false });
      }
      return signedPdfBytes.buffer;

    } catch (err: any) {
      console.error('Error in generateSignedPdf:', err);
      throw err;
    }
  }

  /** Collect browser and device details for the audit fingerprint. */
  collectDeviceInfo(): Record<string, any> {
    const nav = window.navigator as any;
    const scr = window.screen;
    return {
      userAgent:           nav.userAgent || '',
      platform:            nav.platform || '',
      language:            nav.language || '',
      languages:           (nav.languages || []).join(', '),
      timezone:            Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      timezoneOffset:      new Date().getTimezoneOffset(),
      screenResolution:    `${scr.width}x${scr.height}`,
      screenColorDepth:    scr.colorDepth,
      deviceMemoryGB:      nav.deviceMemory || null,
      hardwareConcurrency: nav.hardwareConcurrency || null,
      online:              nav.onLine,
      cookiesEnabled:      nav.cookieEnabled,
      doNotTrack:          nav.doNotTrack || null,
      vendor:              nav.vendor || '',
      maxTouchPoints:      nav.maxTouchPoints ?? 0,
      capturedAt:          new Date(Date.now() + this.timeOffset).toISOString(),
      geolocation:         this.geolocation,
    };
  }

  // IOHB-1779: what this signer has entered in their fill-in fields (by PDF field name), and the required
  // ones that were still empty when they last tried to submit.
  fillFieldValues: Record<string, string | boolean> = {};
  emptyRequiredFillFields: string[] = [];
  // IOHB-1868: the export values of each checkbox field's boxes, by field name. Boxes that share a name but have
  // different export values are one question with several answers (e.g. Yes / No), so such a field records the
  // ticked box's export value (or false) instead of true / false.
  private checkboxExportValues: Record<string, string[]> = {};

  // IOHB-1884: the signer's fill-in fields and signature boxes in the order they appear in the form, worked out each time
  // the pages are drawn (empty until then), and the fill-in field the signer is on (null while on a signature).
  signingStops: SigningStop[] = [];
  activeFillFieldName: string | null = null;

  // IOHB-1886: the labels for the fields this signer fills in, and what kind of field each is, noted as the field is
  // unlocked. A resting label only shows where it covers nothing on the page, so the printed text on each drawn page (as
  // fractions of the page's size, read once per page) and what else labels must not cover are kept.
  fillFieldTags: FillFieldTag[] = [];
  private fillFieldKinds: Record<string, FillFieldKind> = {};
  private printedText: Record<number, PageBox[]> = {};
  private printedTextItems: { doc: any; pages: Record<number, Promise<any[]>> } | null = null;
  private fillTagObstacles: PageObstacle[] = [];
  private fillTagMeasure?: CanvasRenderingContext2D | null;

  private fillFieldNameSet(): Set<string> {
    return new Set(
      (this.linkDetails?.fillFields || []).map((f: any) => f?.name).filter((n: any) => typeof n === 'string' && n !== ''),
    );
  }

  /** The signer's unlocked fill-in field inputs on the drawn pages, with the page each is on and where it sits on it. */
  private positionedFillInputs(): { el: HTMLElement; name: string; page: number; top: number; left: number; width: number; height: number }[] {
    const inputs: { el: HTMLElement; name: string; page: number; top: number; left: number; width: number; height: number }[] = [];
    for (let page = 1; page <= (this.numPages || 0); page++) {
      const layer = document.getElementById('pdf-annotations-' + page);
      if (!layer) continue;
      const layerRect = layer.getBoundingClientRect();
      layer.querySelectorAll('[data-fill-field]').forEach((el: any) => {
        const rect = (el.closest('section') || el).getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return; // not shown on the page
        inputs.push({
          el,
          name: el.dataset.fillField,
          page,
          top: rect.top - layerRect.top,
          left: rect.left - layerRect.left,
          width: rect.width,
          height: rect.height,
        });
      });
    }
    return inputs;
  }

  /** Puts the signer's fill-in fields and signature boxes in form order (IOHB-1884). A field shown in more than one
   *  place, or a set of radio buttons, is one stop, where it first appears. */
  updateSigningStops() {
    const positioned: { stop: SigningStop; page: number; top: number; left: number }[] = [];
    (this.sigFieldsList || []).forEach((sigBox: any, index: number) => {
      positioned.push({
        stop: { kind: 'signature', index },
        page: Number(sigBox?.pageNumber) || 0,
        top: Number(sigBox?.displayY) || 0,
        left: Number(sigBox?.displayX) || 0,
      });
    });
    const fillInputs = this.positionedFillInputs();
    fillInputs.forEach(input => {
      positioned.push({ stop: { kind: 'fill', name: input.name }, page: input.page, top: input.top, left: input.left });
    });
    positioned.sort(byFormOrder);

    const seenFillFields = new Set<string>();
    this.signingStops = positioned
      .map(p => p.stop)
      .filter(stop => {
        if (stop.kind === 'signature') return true;
        if (seenFillFields.has(stop.name)) return false;
        seenFillFields.add(stop.name);
        return true;
      });
    this.updateFillFieldTags(fillInputs);
  }

  /** Makes a label for each field this signer fills in (IOHB-1886): one per field on each page, where the field first
   *  appears there, so a set of radio buttons gets one label. */
  private updateFillFieldTags(fillInputs: { name: string; page: number; top: number; left: number; width: number; height: number }[]) {
    const labelled = new Set<string>();
    this.fillFieldTags = [...fillInputs]
      .sort(byFormOrder)
      .filter(input => {
        const key = `${input.page}:${input.name}`;
        if (labelled.has(key)) return false;
        labelled.add(key);
        return true;
      })
      .map(input => {
        const kind = this.fillFieldKinds[input.name] || 'text';
        const text = fillFieldTagText(kind, this.isRequiredFillField(input.name));
        const layer = document.getElementById('pdf-annotations-' + input.page);
        const pageWidth = layer?.getBoundingClientRect().width || 0;
        const pageHeight = layer?.getBoundingClientRect().height || 0;
        const width = this.fillTagWidth(text, layer);
        const { spots, forced } = currentFieldSpots(input, kind, width, pageWidth, pageHeight);
        return {
          name: input.name,
          kind,
          page: input.page,
          top: input.top,
          left: input.left,
          text,
          above: labelBox(input, width, pageWidth, 'above'),
          currentSpots: spots,
          forced,
          pageHeight,
          labelTop: null,
          labelLeft: input.left,
          shown: false,
        };
      });
    this.fillTagObstacles = this.fillTagObstaclesOnPages();
    this.showFillFieldTagsWithRoom();
  }

  /** How wide a label is drawn: its text in the signature labels' 9px bold type, its padding and a little to spare. */
  private fillTagWidth(text: string, layer: HTMLElement | null): number {
    this.fillTagMeasure ??= document.createElement('canvas').getContext('2d');
    if (!this.fillTagMeasure || !layer) return text.length * 6 + 12;
    this.fillTagMeasure.font = `bold 9px ${getComputedStyle(layer.parentElement || layer).fontFamily}`;
    return Math.ceil(this.fillTagMeasure.measureText(text).width) + 12;
  }

  /** What labels must not cover on the drawn pages: printed text, every field (named when this signer fills it in) and
   *  the signature boxes with their own labels (and, on a phone, the "Sign here" tag). */
  private fillTagObstaclesOnPages(): PageObstacle[] {
    const obstacles: PageObstacle[] = [];
    for (let page = 1; page <= (this.numPages || 0); page++) {
      const layer = document.getElementById('pdf-annotations-' + page);
      if (!layer) continue;
      const { left, top, width, height } = layer.getBoundingClientRect();
      (this.printedText[page] || []).forEach(text => {
        obstacles.push({
          page,
          box: { left: text.left * width, top: text.top * height, width: text.width * width, height: text.height * height },
        });
      });
      layer.querySelectorAll('input, textarea, select, button').forEach((el: any) => {
        const rect = (el.closest('section') || el).getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return; // not shown on the page
        obstacles.push({
          page,
          name: el.dataset?.fillField,
          box: { left: rect.left - left, top: rect.top - top, width: rect.width, height: rect.height },
        });
      });
    }
    const roomAboveSignature = this.isMobile ? SIGN_HERE_TAG_ROOM : LABEL_ROOM;
    (this.sigFieldsList || []).forEach((sigBox: any) => {
      obstacles.push({
        page: Number(sigBox?.pageNumber) || 0,
        box: {
          left: Number(sigBox?.displayX) || 0,
          top: (Number(sigBox?.displayY) || 0) - roomAboveSignature,
          width: Number(sigBox?.displayWidth) || 0,
          height: (Number(sigBox?.displayHeight) || 0) + roomAboveSignature,
        },
      });
    });
    return obstacles;
  }

  /** Decides which labels show and where (IOHB-1886). The field the signer is on always shows its label and is placed
   *  first: in the first of its spots with room (see currentFieldSpots), else in its forced spot. Every other label
   *  shows above its field only where it covers nothing. A checkbox's label only shows while the signer is on it, so
   *  rows of checkboxes don't stack labels over the form. */
  private showFillFieldTagsWithRoom() {
    const current = this.fillFieldTags.filter(tag => tag.name === this.activeFillFieldName);
    const resting = this.fillFieldTags.filter(tag => tag.name !== this.activeFillFieldName && tag.kind !== 'checkbox');
    const places = placeLabels(
      [...current.map(tag => ({ ...tag, spots: tag.currentSpots })), ...resting.map(tag => ({ ...tag, spots: [tag.above], forced: undefined }))],
      this.fillTagObstacles,
    );
    this.fillFieldTags.forEach(tag => {
      tag.shown = false;
      tag.labelTop = null;
    });
    [...current, ...resting].forEach((tag, index) => {
      const box = places[index];
      if (!box) return;
      tag.shown = true;
      // A label above its field sits at the top of its room, one below it sits under the gap
      tag.labelTop = box.top >= tag.top ? box.top + LABEL_GAP : box.top;
      tag.labelLeft = box.left;
    });
  }

  /** The pages the empty required fields are on, for the warning before submitting: "page 3", "pages 3 and 5", or ""
   *  while the pages aren't drawn (IOHB-1886). */
  emptyRequiredFieldPages(): string {
    const pages: number[] = [];
    this.emptyRequiredFillFields.forEach(name => {
      const page = this.fillFieldTags.find(tag => tag.name === name)?.page;
      if (page !== undefined && !pages.includes(page)) pages.push(page);
    });
    pages.sort((a, b) => a - b);
    if (pages.length === 0) return '';
    if (pages.length === 1) return `page ${pages[0]}`;
    return `pages ${pages.slice(0, -1).join(', ')} and ${pages[pages.length - 1]}`;
  }

  /** "Go back and fill in": closes the warning and takes the signer to the first empty required field (IOHB-1886). */
  goBackToEmptyRequiredField() {
    const first = this.fillFieldTags.find(tag => this.emptyRequiredFillFields.includes(tag.name));
    this.emptyRequiredFillFields = [];
    if (first) this.goToStop({ kind: 'fill', name: first.name }, false);
  }

  /** Where a drawn page's printed text is, as fractions of the page's size (IOHB-1886). pdf.js reads a page's text once
   *  per document; a page whose text can't be read gives none. */
  private async printedTextBoxes(page: any, pageNum: number, viewport: any): Promise<PageBox[]> {
    try {
      if (this.printedTextItems?.doc !== this.pdfDoc) this.printedTextItems = { doc: this.pdfDoc, pages: {} };
      const pages = this.printedTextItems.pages;
      pages[pageNum] ??= Promise.resolve(page.getTextContent()).then((content: any) => content?.items || []);
      const items = await pages[pageNum];
      const pdfjsUtil = (window as any).pdfjsLib.Util;
      return items
        .filter((item: any) => typeof item?.str === 'string' && item.str.trim() !== '' && Array.isArray(item.transform))
        .map((item: any) => {
          const [, , c, d, x, baseline] = pdfjsUtil.transform(viewport.transform, item.transform);
          const fontHeight = Math.hypot(c, d);
          return {
            left: x / viewport.width,
            top: (baseline - fontHeight) / viewport.height,
            width: (item.width * viewport.scale) / viewport.width,
            height: (fontHeight * 1.25) / viewport.height,
          };
        });
    } catch {
      return [];
    }
  }

  /** Label colours as for signatures: yellow for the field the signer is on, green once it's filled in, ticked or
   *  chosen, and blue while it's still to do. */
  fillFieldTagColor(tag: FillFieldTag): string {
    if (tag.name === this.activeFillFieldName) return '#fbc02d';
    const value = this.fillFieldValues[tag.name];
    const done = tag.kind === 'checkbox' ? value === true : typeof value === 'string' && value.trim() !== '';
    return done ? '#5CB85C' : '#2D388B';
  }

  /** Scrolls a fill-in field high into view, as signatures are, and on a computer puts the cursor in it. On a phone the
   *  signer taps it themselves, so the keyboard doesn't cover the form before they're ready. */
  scrollToFillField(name: string) {
    const input = this.positionedFillInputs().filter(i => i.name === name).sort(byFormOrder)[0];
    if (!input || !this.pdfViewerScroll) return;
    const scrollEl = this.pdfViewerScroll.nativeElement;
    const scrollRect = scrollEl.getBoundingClientRect();
    const rect = (input.el.closest('section') || input.el).getBoundingClientRect();
    const scrollDivisor = this.isMobile ? 6 : 3;
    scrollEl.scrollTo({
      top: scrollEl.scrollTop + rect.top - scrollRect.top - scrollRect.height / scrollDivisor + rect.height / 2,
      behavior: 'smooth',
    });
    if (!this.isMobile) input.el.focus({ preventScroll: true });
  }

  /** Notes which fill-in field the signer is on (null: they're on a signature), outlines it on the page and gives its label
   *  first claim on the room above it (IOHB-1886). */
  private setActiveFillField(name: string | null) {
    this.activeFillFieldName = name;
    document.querySelectorAll('[data-fill-field]').forEach((el: any) => {
      el.closest('section')?.classList.toggle('esign-fill-active', name !== null && el.dataset.fillField === name);
    });
    this.showFillFieldTagsWithRoom();
  }

  private isRequiredFillField(fieldName: string): boolean {
    return (this.linkDetails?.fillFields || []).some((f: any) => f?.name === fieldName && f.required === true);
  }

  /** Notes the export values of the checkbox boxes pdf.js reports, from the document's field objects and the page's
   *  annotations (IOHB-1868). */
  private noteCheckboxExportValues(fieldObjects: any, annotations: any[]) {
    const note = (fieldName: any, exportValue: any) => {
      if (typeof fieldName !== 'string' || fieldName === '' || exportValue === undefined || exportValue === null || exportValue === 'Off') return;
      const values = this.checkboxExportValues[fieldName] || (this.checkboxExportValues[fieldName] = []);
      if (!values.includes(String(exportValue))) values.push(String(exportValue));
    };
    Object.entries(fieldObjects || {}).forEach(([fieldName, boxes]: [string, any]) => {
      if (Array.isArray(boxes)) boxes.forEach((box: any) => { if (box?.type === 'checkbox') note(fieldName, box.exportValues); });
    });
    (annotations || []).forEach((annotation: any) => { if (annotation?.checkBox) note(annotation.fieldName, annotation.exportValue); });
  }

  /** True for a checkbox field whose boxes have different export values: a pick-one field (IOHB-1868). */
  private isPickOneCheckbox(fieldName: string): boolean {
    return (this.checkboxExportValues[fieldName]?.length || 0) > 1;
  }

  /** Required fill-in fields with nothing entered: blank text, no choice made, or an unticked box. */
  findEmptyRequiredFillFields(): string[] {
    return (this.linkDetails?.fillFields || [])
      .filter((f: any) => f?.required === true && typeof f.name === 'string')
      .filter((f: any) => {
        const value = this.fillFieldValues[f.name];
        return value === undefined || value === false || (typeof value === 'string' && value.trim() === '');
      })
      .map((f: any) => f.name);
  }

  /** What kind of field a drawn fill-in input is, for its label (IOHB-1886). */
  private fillFieldKindOf(el: any, fieldName: string): FillFieldKind {
    if (el.type === 'radio' || (el.type === 'checkbox' && this.isPickOneCheckbox(fieldName))) return 'pickOne';
    if (el.type === 'checkbox') return 'checkbox';
    return el.tagName === 'SELECT' ? 'list' : 'text';
  }

  /** Makes one of this signer's fill-in fields editable, shows what they've entered so far (the page is
   *  re-rendered on zoom), and keeps fillFieldValues in step with it. */
  private unlockFillField(el: any, section: HTMLElement | null, fieldName: string, annotation: any) {
    el.disabled = false;
    el.removeAttribute('disabled');
    el.readOnly = false;
    el.removeAttribute('readonly');
    el.setAttribute('tabindex', '0');
    // What the field is, for its label; a readable name for screen readers, and whether it's required; and guide text in
    // an empty text box (IOHB-1886).
    const kind = this.fillFieldKindOf(el, fieldName);
    this.fillFieldKinds[fieldName] = kind;
    el.setAttribute('aria-label', fillFieldLabel(fieldName, annotation?.alternativeText) ?? fieldName);
    // (A radio button takes no aria-required: ARIA gives it only to the group, which pdf.js doesn't draw.)
    if (el.type !== 'radio') el.setAttribute('aria-required', String(this.isRequiredFillField(fieldName)));
    if (kind === 'text' && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && !el.placeholder) el.placeholder = 'Type here';
    el.style.pointerEvents = 'auto';
    el.style.cursor = el.type === 'checkbox' || el.type === 'radio' ? 'pointer' : 'text';
    section?.classList.add('esign-fill-field');
    if (this.isRequiredFillField(fieldName)) section?.classList.add('esign-fill-required');
    if (fieldName === this.activeFillFieldName) section?.classList.add('esign-fill-active');

    const choiceValue = String(annotation?.buttonValue ?? annotation?.exportValue ?? el.value ?? '');
    const stored = this.fillFieldValues[fieldName];
    if (el.type === 'checkbox' && this.isPickOneCheckbox(fieldName)) {
      // One of several answers (IOHB-1868): start from the answer already in the PDF, and show only that box ticked.
      if (stored === undefined) {
        const current = annotation?.fieldValue;
        this.fillFieldValues[fieldName] = typeof current === 'string' && current !== '' && current !== 'Off' ? current : false;
      }
      el.checked = this.fillFieldValues[fieldName] === choiceValue;
    } else if (el.type === 'checkbox') {
      if (stored === undefined) this.fillFieldValues[fieldName] = !!el.checked;
      else el.checked = stored === true;
    } else if (el.type === 'radio') {
      if (stored === undefined) {
        if (el.checked) this.fillFieldValues[fieldName] = choiceValue;
      } else {
        el.checked = stored === choiceValue;
      }
    } else if (stored === undefined) {
      this.fillFieldValues[fieldName] = el.value ?? '';
    } else {
      el.value = stored;
    }

    if (el.dataset?.fillListener) return;
    if (el.dataset) {
      el.dataset.fillListener = 'true';
      el.dataset.fillField = fieldName;
      el.dataset.fillChoice = choiceValue;
    }
    const sync = () => {
      const pickOne = el.type === 'checkbox' && this.isPickOneCheckbox(fieldName);
      if (pickOne) {
        if (el.checked) this.fillFieldValues[fieldName] = choiceValue;
        else if (this.fillFieldValues[fieldName] === choiceValue) this.fillFieldValues[fieldName] = false;
      } else if (el.type === 'checkbox') {
        this.fillFieldValues[fieldName] = !!el.checked;
      } else if (el.type === 'radio') {
        if (el.checked) this.fillFieldValues[fieldName] = choiceValue;
      } else {
        this.fillFieldValues[fieldName] = el.value ?? '';
      }
      // A field can appear in more than one place on the document: keep every copy showing the same value. The boxes
      // of a pick-one field show only the ticked answer (IOHB-1868).
      if (el.type !== 'radio') {
        document.querySelectorAll('[data-fill-field]').forEach((other: any) => {
          if (other === el || other.dataset.fillField !== fieldName) return;
          if (pickOne) other.checked = this.fillFieldValues[fieldName] === other.dataset.fillChoice;
          else if (el.type === 'checkbox') other.checked = el.checked;
          else other.value = el.value;
        });
      }
      if (this.emptyRequiredFillFields.length > 0) this.emptyRequiredFillFields = this.findEmptyRequiredFillFields();
    };
    el.addEventListener('input', sync);
    el.addEventListener('change', sync);
    // Tapping or tabbing into a field makes it where "Next" carries on from (IOHB-1884).
    el.addEventListener('focus', () => {
      if (this.activeFillFieldName === fieldName) return;
      this.setActiveFillField(fieldName);
      this.cdr.detectChanges();
    });
  }

  /** Writes what this signer entered into their fill-in fields and makes those fields read-only, so a
   *  later signer sees the values but can't change them. A field that was never shown keeps its value, a
   *  field an earlier signer already locked is left alone, and a value that can't be written stays editable
   *  rather than being locked blank. */
  private applyFillFieldValues(form: any) {
    for (const name of this.fillFieldNameSet()) {
      let field: any = null;
      try {
        field = form.getField(name);
      } catch {
        field = null;
      }
      if (!field) continue;
      if (typeof field.isReadOnly === 'function' && field.isReadOnly()) continue;
      const value = this.fillFieldValues[name];
      let written = value === undefined;
      try {
        if (value !== undefined) {
          if (typeof field.check === 'function' && typeof field.uncheck === 'function') {
            if (typeof value === 'string' && value !== '') {
              // A pick-one field: tick the box that was chosen, not the field's first box (IOHB-1868)
              written = tickCheckBoxByExportValue(field, value);
            } else {
              if (value === true) field.check(); else field.uncheck();
              written = true;
            }
          } else if (typeof field.select === 'function' && typeof field.getOptions === 'function') {
            const option = this.pdfOptionFor(field, value);
            if (option !== undefined) {
              field.select(option);
              written = true;
            } else if (value === '' && typeof field.clear === 'function') {
              field.clear();
              written = true;
            }
          } else if (typeof field.setText === 'function') {
            field.setText(String(value));
            written = true;
          }
        }
      } catch (e) {
        console.warn(`Could not fill field ${name}:`, e);
      }
      if (!written) {
        console.warn(`Could not write field ${name}, so it was left editable.`);
        continue;
      }
      try {
        if (typeof field.enableReadOnly === 'function') field.enableReadOnly();
      } catch (e) {
        console.warn(`Could not lock field ${name}:`, e);
      }
    }
  }

  /** The option pdf-lib accepts for what the page reported. pdf.js reports a dropdown's export value and a
   *  radio button's on-state name, while pdf-lib selects a dropdown by its display text and a radio group
   *  by its export value, so translate through the field's own option lists, then fall back to the text. */
  private pdfOptionFor(field: any, value: any): string | undefined {
    const wanted = String(value);
    const text = (v: any): string | undefined => (v && typeof v.decodeText === 'function' ? v.decodeText() : undefined);
    const acroField = field.acroField;
    try {
      if (acroField && typeof acroField.getOnValues === 'function') {
        const onValues = acroField.getOnValues() || [];
        const exportValues = typeof acroField.getExportValues === 'function' ? acroField.getExportValues() : undefined;
        const index = onValues.findIndex((v: any) => text(v) === wanted);
        if (index >= 0) return exportValues ? text(exportValues[index]) : wanted;
      } else if (acroField && typeof acroField.getOptions === 'function') {
        const match = (acroField.getOptions() || []).find((o: any) => text(o?.value) === wanted || text(o?.display) === wanted);
        if (match) return text(match.display) ?? text(match.value);
      }
    } catch {
      // fall back to matching the option text below
    }
    const options: string[] = field.getOptions() || [];
    return options.find((o) => o === wanted) ?? options.find((o) => String(o).toLowerCase() === wanted.toLowerCase());
  }

  submitSignature(submitWithEmptyRequiredFields = false) {
    if (!this.consent) {
      this.messageHandlerService.generateToastMessage('error', 'Error', 'You must consent to sign electronically.');
      return;
    }
    if (!this.allSignaturesCompleted()) {
      this.messageHandlerService.generateToastMessage('error', 'Error', 'Please complete all signature fields in the guided process.');
      return;
    }
    // A required fill-in field left empty only warns: the signer can go back or submit anyway.
    const emptyRequired = this.findEmptyRequiredFillFields();
    if (emptyRequired.length > 0 && !submitWithEmptyRequiredFields) {
      this.emptyRequiredFillFields = emptyRequired;
      return;
    }
    this.emptyRequiredFillFields = [];

    const deviceInfo = this.collectDeviceInfo();
    this.submittingSign = true;

    this.generateSignedPdf().then((signedPdfBuffer) => {
      const blob = new Blob([signedPdfBuffer], { type: 'application/pdf' });
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64Pdf = reader.result as string;

        let initialsVal = 'SIG';
        if (this.signatureMode === 'type' && this.typedSignature) {
          const parts = this.typedSignature.trim().split(/\s+/);
          if (parts.length > 0) {
            initialsVal = parts.map(p => p[0]).join('').toUpperCase().substring(0, 5);
          }
        }

        const firstSigImage = this.sigFieldsList[0]?.signatureImage || this.getSignatureImage();

        const signSub = this.esocService.signDocument({
          code: this.code,
          token: this.sessionToken,
          initials: initialsVal || 'SIG',
          consent: this.consent,
          signatureImage: firstSigImage,
          signedPdf: base64Pdf,
          deviceInfo,
        }).subscribe({
          next: (res) => {
            this.submittingSign = false;
            if (res?.success) {
              this.messageHandlerService.generateToastMessage('success', 'Signed', 'Document signed successfully.');
              this.isSigned = true;

              const signedDetails: any = {};
              if (res.data) {
                const signedFileName = res.data.signedFileName || res.data.signedFileLocation || res.data.fileName;
                const signedFileLoc = res.data.signedFileLocation || res.data.signedFileName;
                signedDetails.fileLocation = signedFileName;
                signedDetails.signedFileLocation = signedFileLoc;
                signedDetails.signedAt = res.data.signedAt;
                signedDetails.esignatureKey = res.data.esignatureKey;
              }

              // loadLinkDetails() merges signedDetails and re-fetches the signed PDF once —
              // no separate fetchPdfUrl() here, which previously double-loaded the PDF (and
              // double-logged "Document viewed") right after signing.
              this.loadLinkDetails(signedDetails);
            } else {
              this.messageHandlerService.generateToastMessage('error', 'Error', res?.message || 'Failed to sign document.');
            }
          },
          error: (err) => {
            this.submittingSign = false;
            this.messageHandlerService.generateToastMessage('error', 'Error', err?.error?.message || 'Failed to submit signature.');
          }
        });
        this.subscriptions.push(signSub);
      };
    }).catch((err: any) => {
      this.submittingSign = false;
      this.messageHandlerService.generateToastMessage('error', 'Error', err.message || 'Failed to embed signature onto PDF.');
    });
  }

  downloadDocument() {
    // Prefer the signed document location if already signed, else fall back to template
    const signedKey = this.linkDetails?.signedFileLocation || this.linkDetails?.fileLocation;
    if (signedKey) {
      if (this.code) {
        this.esocService.logEsignAuditEvent({
          code: this.code,
          eventType: 'DOCUMENT_DOWNLOADED',
          eventData: {
            fileName: signedKey || this.linkDetails?.fileName || 'signed-document.pdf'
          }
        }).subscribe();
      }
      const params: any = { fileName: signedKey };
      if (this.code) params.code = this.code;
      if (this.sessionToken) params.token = this.sessionToken;

      const downloadSub = this.esocService.getEPHfileObjectUrl(params).subscribe((res) => {
        if (res.success) {
          const link = document.createElement('a');
          link.href = res.url;
          link.target = '_blank';
          link.download = this.linkDetails?.fileName || 'signed-document.pdf';
          link.click();
        }
      });
      this.subscriptions.push(downloadSub);
    } else if (this.rawPdfUrl) {
      if (this.code) {
        this.esocService.logEsignAuditEvent({
          code: this.code,
          eventType: 'DOCUMENT_DOWNLOADED',
          eventData: {
            fileName: this.linkDetails?.fileName || 'signed-document.pdf'
          }
        }).subscribe();
      }
      const link = document.createElement('a');
      link.href = this.rawPdfUrl;
      link.target = '_blank';
      link.download = this.linkDetails?.fileName || 'signed-document.pdf';
      link.click();
    }
  }

  ngOnDestroy(): void {
    if (this.timeIntervalId) {
      clearInterval(this.timeIntervalId);
    }
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
